import React, {
  CSSProperties,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { useQuery } from "react-query";
import axios from "axios";
import ContentHeader from "../../components/Admin/ContentHeader";
import CustomButton from "../../components/Admin/CustomButton/CustomButton";
import BranchQueueFilter from "../../components/Admin/BranchQueueFilter/BranchQueueFilter";
import BranchQueueTreeSelect from "../../components/Admin/BranchQueueTreeSelect/BranchQueueTreeSelect";
import { Button } from "../../components/Admin/Button/Button";
import { useEffectiveBranchQueueId } from "../../hooks/Admin/useEffectiveBranchQueueId";
import { useGetTerminalAppearance } from "../../hooks/Admin/TerminalAppearance/useGetTerminalAppearance";
import { usePutTerminalAppearance } from "../../hooks/Admin/TerminalAppearance/usePutTerminalAppearance";
import { AdvertisingAPI, BranchAPI } from "../../constants/API";
import { commonHeaders } from "../../constants/HeadersAPI";
import type { BranchQueueItemDto } from "../../hooks/Admin/WorkingPlacesTable/useGetQueuesForCurrentUser";
import { usePostTerminalBackground } from "../../hooks/Admin/TerminalAppearance/usePostTerminalBackground";
import { useDeleteTerminalBackground } from "../../hooks/Admin/TerminalAppearance/useDeleteTerminalBackground";
import { usePostTabloLogo } from "../../hooks/Admin/TerminalAppearance/usePostTabloLogo";
import { usePostTabloSliderImage } from "../../hooks/Admin/TerminalAppearance/usePostTabloSliderImage";
import { usePostTabloVideo } from "../../hooks/Admin/TerminalAppearance/usePostTabloVideo";
import classes from "./TabloSettingsPage.module.scss";
import {
  TerminalAppearanceSettings,
  TerminalBackgroundMedia,
  TerminalLogoSettings,
  TerminalTabloElementSettings,
  TerminalTabloSettings,
} from "../../types/Admin/TerminalAppearanceTypes";
import { Rnd } from "react-rnd";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { baseSoundURL } from "../../config";
import tehnoSkyTerminalLogo from "../../assets/icons/tehnoSkyTerminalLogo.svg";
import { AlertContext } from "../../context/AlertProvider";
import { HeaderCalendarIcon } from "../../assets/svgComponents/HeaderCalendarIcon";
import { HeaderClockIcon } from "../../assets/svgComponents/HeaderClockIcon";
import { QueueTicketIcon } from "../../assets/svgComponents/QueueTicketIcon";
import { QueueWindowIcon } from "../../assets/svgComponents/QueueWindowIcon";
import { QueueArrowIcon } from "../../assets/svgComponents/QueueArrowIcon";
import { QueueTicketClook } from "../../assets/svgComponents/QueueTicketClook";
import { useMyPermissions } from "../../hooks/useMyPermissions";
import { useAdminQueueIdsFilter } from "../../hooks/Admin/useAdminQueueIdsFilter";

const defaultQueueVideoVolume = 20;

const defaultTabloLogoFitMode: NonNullable<TerminalLogoSettings["fitMode"]> =
  "scale";
const defaultTabloLogoScale = 100;
const defaultTabloLogoPosition = 50;

const normalizeMediaUrl = (url?: string | null) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("//")) return url;
  const separator = url.startsWith("/") ? "" : "/";
  return `${baseSoundURL}${separator}${url}`;
};

const getLogoBackgroundSize = (
  fitMode: string | null | undefined,
  scale: number,
) => {
  switch (fitMode) {
    case "contain":
      return "contain";
    case "cover":
      return "cover";
    case "stretch":
      return "100% 100%";
    case "width":
      return "100% auto";
    case "height":
      return "auto 100%";
    case "none":
      return "auto";
    case "scale":
    default:
      return `${scale}% ${scale}%`;
  }
};

const parseNumberInput = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (trimmed === "") return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
};

const toNumberInputValue = (value: number | null | undefined, fallback = "") =>
  value != null && Number.isFinite(value) ? String(value) : fallback;

const fontOptions = [
  { label: "SFPro", value: "SFPro" },
  { label: "Inter", value: "Inter" },
  { label: "Montserrat", value: "Montserrat" },
  { label: "Roboto Regular", value: "Roboto-400" },
  { label: "Roboto Medium", value: "Roboto" },
  { label: "Bebas", value: "Bebas" },
];

const fontWeights = [
  { label: "Regular", value: "400" },
  { label: "Medium", value: "600" },
  { label: "Bold", value: "700" },
];

type TabloElementKey = keyof TerminalTabloSettings["elements"];

type TabloSectionKey =
  | "preview"
  | "logo"
  | "background"
  | "elements"
  | "volume";

const elementLabels: Record<TabloElementKey, string> = {
  header: "Шапка",
  video: "Видео",
  title: "Заголовок и подзаголовок",
  queue: "Талон/Окно",
  takenTickets: "Ожидание",
  ticker: "Бегущая строка",
  slider: "Слайдер",
  weatherWidget: "Виджет погоды",
  rssBeltaWidget: "RSS лента (БЕЛТА)",
};

const elementHasText: Record<TabloElementKey, boolean> = {
  header: true,
  video: false,
  title: true,
  queue: true,
  takenTickets: true,
  ticker: true,
  slider: false,
  weatherWidget: false,
  rssBeltaWidget: false,
};

const elementHasNumberSize: Record<TabloElementKey, boolean> = {
  header: false,
  video: false,
  title: false,
  queue: true,
  takenTickets: true,
  ticker: false,
  slider: false,
  weatherWidget: false,
  rssBeltaWidget: false,
};

const clampNumber = (value: number, min: number, max: number) => {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.min(max, Math.max(min, value));
};

const normalizeHexColor = (value: string, fallback = "#000000") => {
  const cleaned = value.trim().replace("#", "");
  if (![3, 6].includes(cleaned.length) || !/^[0-9a-fA-F]+$/.test(cleaned)) {
    return fallback;
  }
  let full = cleaned;
  if (cleaned.length === 3) {
    full = cleaned
      .split("")
      .map((char) => char + char)
      .join("");
  }
  return `#${full.toLowerCase()}`;
};

const hexToRgb = (hex: string) => {
  const normalized = normalizeHexColor(hex);
  const raw = normalized.replace("#", "");
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16),
  };
};

const parseColorWithAlpha = (
  value?: string | null,
  fallbackHex = "#0b0f1a",
) => {
  if (!value) {
    return { hex: fallbackHex, alpha: 1 };
  }
  const trimmed = value.trim();
  if (trimmed.startsWith("#")) {
    return { hex: normalizeHexColor(trimmed, fallbackHex), alpha: 1 };
  }
  const rgbaMatch = trimmed.match(/rgba?\(([^)]+)\)/i);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(",").map((part) => part.trim());
    const r = Number(parts[0]);
    const g = Number(parts[1]);
    const b = Number(parts[2]);
    if (![r, g, b].every((channel) => Number.isFinite(channel))) {
      return { hex: fallbackHex, alpha: 1 };
    }
    const hex = `#${[r, g, b]
      .map((channel) =>
        clampNumber(Math.round(channel), 0, 255).toString(16).padStart(2, "0"),
      )
      .join("")}`;
    const alpha = parts.length > 3 ? clampNumber(Number(parts[3]), 0, 1) : 1;
    return {
      hex: normalizeHexColor(hex, fallbackHex),
      alpha: Number.isFinite(alpha) ? alpha : 1,
    };
  }
  return { hex: fallbackHex, alpha: 1 };
};

const buildColorWithAlpha = (hex: string, alpha: number) => {
  const normalizedHex = normalizeHexColor(hex);
  const clamped = clampNumber(alpha, 0, 1);
  if (clamped >= 1) {
    return normalizedHex;
  }
  const { r, g, b } = hexToRgb(normalizedHex);
  const roundedAlpha = Math.round(clamped * 100) / 100;
  return `rgba(${r}, ${g}, ${b}, ${roundedAlpha})`;
};

const normalizeElement = (
  fallback: TerminalTabloElementSettings,
  element?: TerminalTabloElementSettings | null,
): TerminalTabloElementSettings => {
  const fallbackNumberWidth = fallback.numberWidth ?? null;
  const fallbackNumberHeight = fallback.numberHeight ?? null;
  const resolvedNumberWidth = element?.numberWidth ?? fallbackNumberWidth;
  const resolvedNumberHeight = element?.numberHeight ?? fallbackNumberHeight;
  const result: TerminalTabloElementSettings = {
    ...fallback,
    ...element,
    x: clampNumber(element?.x ?? fallback.x, 0, 100),
    y: clampNumber(element?.y ?? fallback.y, 0, 100),
    width: clampNumber(element?.width ?? fallback.width, 1, 100),
    height: clampNumber(element?.height ?? fallback.height, 1, 100),
    numberWidth: Number.isFinite(resolvedNumberWidth)
      ? clampNumber(Number(resolvedNumberWidth), 40, 1000)
      : null,
    numberHeight: Number.isFinite(resolvedNumberHeight)
      ? clampNumber(Number(resolvedNumberHeight), 24, 600)
      : null,
    isEnabled: element?.isEnabled ?? fallback.isEnabled,
    fontSize: Number.isFinite(element?.fontSize ?? fallback.fontSize)
      ? Number(element?.fontSize ?? fallback.fontSize)
      : fallback.fontSize,
    backdropBlur: Number.isFinite(
      element?.backdropBlur ?? fallback.backdropBlur,
    )
      ? Number(element?.backdropBlur ?? fallback.backdropBlur)
      : fallback.backdropBlur,
    videoFullScreenWhenNoTickets:
      element?.videoFullScreenWhenNoTickets ??
      fallback.videoFullScreenWhenNoTickets ??
      false,
    queueTwoColumns:
      element?.queueTwoColumns ?? fallback.queueTwoColumns ?? false,
  };
  if ("queueTwoColumns" in fallback) {
    result.ticketBlinkBGColor =
      element?.ticketBlinkBGColor ??
      fallback.ticketBlinkBGColor ??
      "rgba(76, 175, 80, 0.6)";
    result.ticketBGColor =
      element?.ticketBGColor ?? fallback.ticketBGColor ?? "#0066ff";
  }
  if ("takenTicketsSortOldToNew" in fallback) {
    result.takenTicketsSortOldToNew =
      element?.takenTicketsSortOldToNew ??
      fallback.takenTicketsSortOldToNew ??
      false;
  }
  if ("imageUrls" in fallback) {
    result.imageUrls = Array.isArray(element?.imageUrls)
      ? element.imageUrls
      : ((fallback as TerminalTabloElementSettings).imageUrls ?? []);
    result.fitMode =
      element?.fitMode ??
      (fallback as TerminalTabloElementSettings).fitMode ??
      "contain";
    const rawInterval =
      element?.slideIntervalSeconds ??
      (fallback as TerminalTabloElementSettings).slideIntervalSeconds ??
      5;
    result.slideIntervalSeconds = Number.isFinite(rawInterval)
      ? Math.min(120, Math.max(1, Number(rawInterval)))
      : 5;
  }
  if ("weatherRegion" in fallback) {
    result.weatherRegion =
      element?.weatherRegion ??
      (fallback as TerminalTabloElementSettings).weatherRegion ??
      "";
    const rawIconSize =
      element?.weatherIconSize ??
      (fallback as TerminalTabloElementSettings).weatherIconSize ??
      40;
    result.weatherIconSize = Number.isFinite(rawIconSize)
      ? Math.min(200, Math.max(16, Number(rawIconSize)))
      : 40;
    const f = fallback as TerminalTabloElementSettings;
    const clampFont = (
      v: number | null | undefined,
      def: number,
      min = 10,
      max = 72,
    ) => (Number.isFinite(v) ? Math.min(max, Math.max(min, Number(v))) : def);
    result.weatherRegionFontSize = clampFont(
      element?.weatherRegionFontSize ?? f.weatherRegionFontSize,
      14,
    );
    result.weatherTempFontSize = clampFont(
      element?.weatherTempFontSize ?? f.weatherTempFontSize,
      28,
    );
    result.weatherDescriptionFontSize = clampFont(
      element?.weatherDescriptionFontSize ?? f.weatherDescriptionFontSize,
      12,
    );
  }
  if ("rssNewsCount" in fallback) {
    const f = fallback as TerminalTabloElementSettings;
    const rawCount = element?.rssNewsCount ?? f.rssNewsCount ?? 5;
    result.rssNewsCount = Number.isFinite(rawCount)
      ? Math.min(20, Math.max(1, Number(rawCount)))
      : 5;
    const rawSwitch =
      element?.rssSwitchIntervalSeconds ?? f.rssSwitchIntervalSeconds ?? 10;
    result.rssSwitchIntervalSeconds = Number.isFinite(rawSwitch)
      ? Math.min(120, Math.max(1, Number(rawSwitch)))
      : 10;
    const rawRefresh =
      element?.rssRefreshPeriodMinutes ?? f.rssRefreshPeriodMinutes ?? 10;
    result.rssRefreshPeriodMinutes = Number.isFinite(rawRefresh)
      ? Math.min(120, Math.max(1, Number(rawRefresh)))
      : 10;
  }
  return result;
};

const defaultTabloSettings: TerminalTabloSettings = {
  backgroundColor: "#0b0f1a",
  backgroundId: null,
  backgroundFitMode: "cover",
  resolutionWidth: 1920,
  resolutionHeight: 1080,
  logo: {
    url: null,
    fitMode: defaultTabloLogoFitMode,
    scale: defaultTabloLogoScale,
    positionX: defaultTabloLogoPosition,
    positionY: defaultTabloLogoPosition,
  },
  elements: {
    header: {
      x: 0,
      y: 0,
      width: 100,
      height: 8,
      isEnabled: true,
      fontFamily: "SFPro",
      fontWeight: "600",
      fontSize: 20,
      textColor: "#ffffff",
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      backdropBlur: 20,
    },
    video: {
      x: 0,
      y: 10,
      width: 60,
      height: 55,
      isEnabled: true,
      backgroundColor: "rgba(0, 0, 0, 0.15)",
      backdropBlur: 0,
      videoFullScreenWhenNoTickets: false,
    },
    title: {
      x: 0,
      y: 67,
      width: 60,
      height: 18,
      isEnabled: true,
      fontFamily: "SFPro",
      fontWeight: "600",
      fontSize: 36,
      textColor: "#ffffff",
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      backdropBlur: 20,
    },
    queue: {
      x: 60,
      y: 10,
      width: 40,
      height: 75,
      numberWidth: 195,
      numberHeight: 90,
      isEnabled: true,
      fontFamily: "SFPro",
      fontWeight: "700",
      fontSize: 28,
      textColor: "#ffffff",
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      backdropBlur: 20,
      queueTwoColumns: false,
      ticketBlinkBGColor: "rgba(76, 175, 80, 0.6)",
      ticketBGColor: "#0066ff",
    },
    takenTickets: {
      x: 86,
      y: 10,
      width: 12,
      height: 75,
      numberWidth: 195,
      numberHeight: 90,
      isEnabled: false,
      fontFamily: "SFPro",
      fontWeight: "700",
      fontSize: 28,
      textColor: "#ffffff",
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      backdropBlur: 20,
      takenTicketsSortOldToNew: false,
    },
    ticker: {
      x: 0,
      y: 90,
      width: 100,
      height: 10,
      isEnabled: true,
      fontFamily: "SFPro",
      fontWeight: "400",
      fontSize: 22,
      textColor: "#ffffff",
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      backdropBlur: 20,
    },
    slider: {
      x: 62,
      y: 10,
      width: 24,
      height: 55,
      isEnabled: false,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      backdropBlur: 10,
      imageUrls: [],
      fitMode: "contain",
      slideIntervalSeconds: 5,
    },
    weatherWidget: {
      x: 2,
      y: 68,
      width: 18,
      height: 18,
      isEnabled: false,
      fontFamily: "SFPro",
      fontWeight: "600",
      fontSize: 14,
      textColor: "#ffffff",
      backgroundColor: "rgba(44, 44, 44, 0.9)",
      backdropBlur: 8,
      weatherRegion: "Минск",
      weatherIconSize: 40,
      weatherRegionFontSize: 14,
      weatherTempFontSize: 28,
      weatherDescriptionFontSize: 12,
    },
    rssBeltaWidget: {
      x: 2,
      y: 50,
      width: 28,
      height: 16,
      isEnabled: false,
      fontFamily: "SFPro",
      fontWeight: "600",
      fontSize: 16,
      textColor: "#ffffff",
      backgroundColor: "rgba(44, 44, 44, 0.9)",
      backdropBlur: 8,
      rssNewsCount: 5,
      rssSwitchIntervalSeconds: 10,
      rssRefreshPeriodMinutes: 10,
    },
  },
};

const normalizeTabloSettings = (
  tablo?: TerminalTabloSettings | null,
): TerminalTabloSettings => {
  const elements = tablo?.elements ?? defaultTabloSettings.elements;
  const logo = tablo?.logo ?? defaultTabloSettings.logo;
  return {
    backgroundColor:
      tablo?.backgroundColor ?? defaultTabloSettings.backgroundColor,
    backgroundId: Number.isFinite(
      tablo?.backgroundId ?? defaultTabloSettings.backgroundId,
    )
      ? Number(tablo?.backgroundId ?? defaultTabloSettings.backgroundId)
      : null,
    backgroundFitMode:
      tablo?.backgroundFitMode ?? defaultTabloSettings.backgroundFitMode,
    resolutionWidth: Number.isFinite(
      tablo?.resolutionWidth ?? defaultTabloSettings.resolutionWidth,
    )
      ? Number(tablo?.resolutionWidth ?? defaultTabloSettings.resolutionWidth)
      : (defaultTabloSettings.resolutionWidth ?? 1920),
    resolutionHeight: Number.isFinite(
      tablo?.resolutionHeight ?? defaultTabloSettings.resolutionHeight,
    )
      ? Number(tablo?.resolutionHeight ?? defaultTabloSettings.resolutionHeight)
      : (defaultTabloSettings.resolutionHeight ?? 1080),
    logo: {
      url: logo?.url ?? defaultTabloSettings.logo?.url ?? null,
      fitMode: logo?.fitMode ?? defaultTabloLogoFitMode,
      scale: Number.isFinite(logo?.scale)
        ? Number(logo?.scale)
        : defaultTabloLogoScale,
      positionX: Number.isFinite(logo?.positionX)
        ? Number(logo?.positionX)
        : defaultTabloLogoPosition,
      positionY: Number.isFinite(logo?.positionY)
        ? Number(logo?.positionY)
        : defaultTabloLogoPosition,
    },
    elements: {
      header: normalizeElement(
        defaultTabloSettings.elements.header,
        elements.header,
      ),
      video: normalizeElement(
        defaultTabloSettings.elements.video,
        elements.video,
      ),
      title: normalizeElement(
        defaultTabloSettings.elements.title,
        elements.title,
      ),
      queue: normalizeElement(
        defaultTabloSettings.elements.queue,
        elements.queue,
      ),
      takenTickets: normalizeElement(
        defaultTabloSettings.elements.takenTickets,
        elements.takenTickets,
      ),
      ticker: normalizeElement(
        defaultTabloSettings.elements.ticker,
        elements.ticker,
      ),
      slider: normalizeElement(
        defaultTabloSettings.elements.slider,
        elements.slider,
      ),
      weatherWidget: normalizeElement(
        defaultTabloSettings.elements.weatherWidget,
        elements.weatherWidget,
      ),
      rssBeltaWidget: normalizeElement(
        defaultTabloSettings.elements.rssBeltaWidget,
        elements.rssBeltaWidget,
      ),
    },
  };
};

type VideoItem = { id: number; fileName: string };

const TabloSettingsPage = () => {
  const alertContext = useContext(AlertContext);
  const { canEdit } = useMyPermissions();
  const canEditTablo = canEdit("SETTINGS_TABLO");
  const { queueIdsFilter } = useAdminQueueIdsFilter();

  const [filterBranchIds, setFilterBranchIds] = useState<number[]>([]);
  const [filterQueueIds, setFilterQueueIds] = useState<number[]>([]);
  const [saveBindingModalOpen, setSaveBindingModalOpen] = useState(false);
  const [saveModalBranchIds, setSaveModalBranchIds] = useState<number[]>([]);
  const [saveModalQueueIds, setSaveModalQueueIds] = useState<number[]>([]);
  const hasSelectedQueue = filterQueueIds.length > 0;

  const effectiveBranchQueueId = useEffectiveBranchQueueId(
    filterBranchIds,
    filterQueueIds,
  );
  const { terminalAppearanceResponse, isLoadingTerminalAppearance } =
    useGetTerminalAppearance({
      branchQueueId: effectiveBranchQueueId,
    });
  const { updateTerminalAppearance, isUpdateTerminalAppearanceLoading } =
    usePutTerminalAppearance();
  const { data: allVideosResp } = useQuery("allAdvertisingVideos", () =>
    axios.get<{ video: VideoItem[] }>(
      AdvertisingAPI.getAllVideos,
      commonHeaders(),
    ),
  );
  const allVideos: VideoItem[] = allVideosResp?.data?.video ?? [];
  const { uploadTerminalBackground, isUploadTerminalBackgroundLoading } =
    usePostTerminalBackground();
  const { deleteTerminalBackground, isDeleteTerminalBackgroundLoading } =
    useDeleteTerminalBackground();
  const { uploadTabloLogo, isUploadTabloLogoLoading } = usePostTabloLogo();
  const { uploadTabloSliderImage, isUploadTabloSliderImageLoading } =
    usePostTabloSliderImage();
  const { uploadTabloVideo, isUploadTabloVideoLoading } = usePostTabloVideo();
  const [videoVolume, setVideoVolume] = useState(defaultQueueVideoVolume);
  const [sliderDragIndex, setSliderDragIndex] = useState<number | null>(null);
  const [videoDragIndex, setVideoDragIndex] = useState<number | null>(null);
  const [editingWeatherRegionFontSize, setEditingWeatherRegionFontSize] =
    useState<string | null>(null);
  const [editingWeatherTempFontSize, setEditingWeatherTempFontSize] = useState<
    string | null
  >(null);
  const [
    editingWeatherDescriptionFontSize,
    setEditingWeatherDescriptionFontSize,
  ] = useState<string | null>(null);
  const [editingWeatherIconSize, setEditingWeatherIconSize] = useState<
    string | null
  >(null);
  const [editingRssNewsCount, setEditingRssNewsCount] = useState<string | null>(
    null,
  );
  const [editingRssSwitchInterval, setEditingRssSwitchInterval] = useState<
    string | null
  >(null);
  const [editingRssRefreshPeriod, setEditingRssRefreshPeriod] = useState<
    string | null
  >(null);
  const [editingRssFontSize, setEditingRssFontSize] = useState<string | null>(
    null,
  );
  const [form, setForm] = useState<TerminalAppearanceSettings | null>(null);
  const [sectionsExpanded, setSectionsExpanded] = useState<
    Record<TabloSectionKey, boolean>
  >({
    preview: false,
    logo: false,
    background: false,
    elements: false,
    volume: false,
  });
  const [expandedElements, setExpandedElements] = useState<
    Record<TabloElementKey, boolean>
  >(() => {
    const keys = Object.keys(
      defaultTabloSettings.elements,
    ) as TabloElementKey[];
    return keys.reduce(
      (acc, key) => {
        acc[key] = false;
        return acc;
      },
      {} as Record<TabloElementKey, boolean>,
    );
  });
  const [videoPlaylistSelectValue, setVideoPlaylistSelectValue] = useState("");
  const previewRef = useRef<HTMLDivElement | null>(null);
  const previewOrientationTouchedRef = useRef(false);
  type RectPx = { x: number; y: number; width: number; height: number };
  const [previewDraftRects, setPreviewDraftRects] = useState<
    Partial<Record<TabloElementKey, RectPx>>
  >({});
  const [previewGuides, setPreviewGuides] = useState<{
    x: number[];
    y: number[];
  } | null>(null);
  const [selectedQueueTabloOrientation, setSelectedQueueTabloOrientation] =
    useState<"VERTICAL" | "HORIZONTAL" | null>(null);
  const [previewSize, setPreviewSize] = useState({ width: 1920, height: 1080 });
  const [resolutionWidthInput, setResolutionWidthInput] = useState("");
  const [resolutionHeightInput, setResolutionHeightInput] = useState("");
  const [previewTabloOrientation, setPreviewTabloOrientation] = useState<
    "VERTICAL" | "HORIZONTAL"
  >("HORIZONTAL");
  const [previewAspectRatio, setPreviewAspectRatio] = useState<"16:9" | "4:3">(
    "16:9",
  );
  const [tickerSpeedInput, setTickerSpeedInput] = useState("");
  const defaultTickerSpeedSeconds = 50;
  const [previewDateTime, setPreviewDateTime] = useState({
    time: "09:45",
    date: "9 февраля",
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = new Date();
      setPreviewDateTime({
        time: format(now, "HH:mm"),
        date: format(now, "d MMMM yyyy", { locale: ru }),
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    // При смене выбранной очереди сбрасываем флаг ручного выбора и подтягиваем ориентацию
    // из настроек очереди ("Филиалы → Табло очереди → Ориентация"), если она доступна.
    previewOrientationTouchedRef.current = false;
    setSelectedQueueTabloOrientation(null);

    const branchId = filterBranchIds[0];
    const queueId = filterQueueIds[0];
    if (!branchId || !queueId) return;

    let cancelled = false;
    axios
      .get<BranchQueueItemDto[]>(BranchAPI.getQueues(branchId), commonHeaders())
      .then((r) => {
        if (cancelled) return;
        const q = r.data?.find((item) => item.id === queueId) ?? null;
        const o = (q?.tabloOrientation as "VERTICAL" | "HORIZONTAL" | null) ?? null;
        setSelectedQueueTabloOrientation(o);
      })
      .catch(() => {
        if (cancelled) return;
        setSelectedQueueTabloOrientation(null);
      });

    return () => {
      cancelled = true;
    };
  }, [filterBranchIds, filterQueueIds]);

  useEffect(() => {
    if (!terminalAppearanceResponse?.settings) return;
    const currentVolume = terminalAppearanceResponse.settings.videoVolume;
    setVideoVolume(
      Number.isFinite(currentVolume) ? currentVolume : defaultQueueVideoVolume,
    );
    const normalizedTablo = normalizeTabloSettings(
      terminalAppearanceResponse.settings.tablo ?? undefined,
    );
    setForm({
      ...terminalAppearanceResponse.settings,
      tablo: normalizedTablo,
    });
    const rw = normalizedTablo.resolutionWidth ?? 1920;
    const rh = normalizedTablo.resolutionHeight ?? 1080;
    setResolutionWidthInput(String(rw));
    setResolutionHeightInput(String(rh));
    if (!previewOrientationTouchedRef.current) {
      setPreviewTabloOrientation(
        selectedQueueTabloOrientation ?? (rw >= rh ? "HORIZONTAL" : "VERTICAL"),
      );
    }
    // Не перезаписываем previewSize при открытой секции превью — там используется измеренный размер контейнера
    if (!sectionsExpanded.preview) {
      setPreviewSize({ width: Number(rw), height: Number(rh) });
    }
    const tickerSpeed = terminalAppearanceResponse.settings.tickerSpeed;
    if (Number.isFinite(tickerSpeed)) {
      setTickerSpeedInput(String(tickerSpeed));
    } else {
      setTickerSpeedInput(String(defaultTickerSpeedSeconds));
    }
  }, [
    selectedQueueTabloOrientation,
    sectionsExpanded.preview,
    terminalAppearanceResponse,
  ]);

  useEffect(() => {
    if (!sectionsExpanded.preview) return;
    const el = previewRef.current;
    if (!el) return;
    const updateSize = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) {
        setPreviewSize((prev) => {
          if (prev.width === w && prev.height === h) return prev;
          return { width: w, height: h };
        });
      }
    };
    updateSize();
    const raf = requestAnimationFrame(updateSize);
    const raf2 = requestAnimationFrame(updateSize);
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updateSize);
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(raf2);
      observer.disconnect();
    };
  }, [sectionsExpanded.preview]);

  const normalizedVideoVolume = useMemo(() => {
    return Number.isFinite(videoVolume)
      ? Math.min(100, Math.max(0, videoVolume))
      : defaultQueueVideoVolume;
  }, [videoVolume]);

  const tabloSettings = form?.tablo ?? defaultTabloSettings;
  const tabloLogo = tabloSettings.logo ?? defaultTabloSettings.logo;
  const tabloLogoPreview =
    normalizeMediaUrl(tabloLogo?.url ?? form?.logo?.url ?? "") ||
    tehnoSkyTerminalLogo;
  const tabloLogoFitMode = tabloLogo?.fitMode ?? defaultTabloLogoFitMode;
  const tabloLogoScaleValue = Number.isFinite(tabloLogo?.scale)
    ? Number(tabloLogo?.scale)
    : defaultTabloLogoScale;
  const tabloLogoScaleInputValue = toNumberInputValue(tabloLogo?.scale);
  const tabloLogoPositionXValue = Number.isFinite(tabloLogo?.positionX)
    ? Number(tabloLogo?.positionX)
    : defaultTabloLogoPosition;
  const tabloLogoPositionXInputValue = toNumberInputValue(tabloLogo?.positionX);
  const tabloLogoPositionYValue = Number.isFinite(tabloLogo?.positionY)
    ? Number(tabloLogo?.positionY)
    : defaultTabloLogoPosition;
  const tabloLogoPositionYInputValue = toNumberInputValue(tabloLogo?.positionY);
  const tabloBackgroundColor = useMemo(
    () => parseColorWithAlpha(tabloSettings.backgroundColor, "#0b0f1a"),
    [tabloSettings.backgroundColor],
  );
  const backgrounds = terminalAppearanceResponse?.backgrounds ?? [];
  const resolutionWidth = Number.isFinite(tabloSettings.resolutionWidth)
    ? Math.max(320, Math.min(7680, Number(tabloSettings.resolutionWidth)))
    : 1920;
  const resolutionHeight = Number.isFinite(tabloSettings.resolutionHeight)
    ? Math.max(240, Math.min(4320, Number(tabloSettings.resolutionHeight)))
    : 1080;

  const resolutionOrientation: "VERTICAL" | "HORIZONTAL" =
    resolutionWidth >= resolutionHeight ? "HORIZONTAL" : "VERTICAL";

  const previewResolution = useMemo(() => {
    const shouldSwap = previewTabloOrientation !== resolutionOrientation;
    return {
      width: shouldSwap ? resolutionHeight : resolutionWidth,
      height: shouldSwap ? resolutionWidth : resolutionHeight,
    };
  }, [
    previewTabloOrientation,
    resolutionHeight,
    resolutionOrientation,
    resolutionWidth,
  ]);

  const previewAspectRatioValue = useMemo(() => {
    const [w, h] = previewAspectRatio.split(":").map((n) => Number(n));
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
      return `${previewResolution.width} / ${previewResolution.height}`;
    }
    return previewTabloOrientation === "HORIZONTAL" ? `${w} / ${h}` : `${h} / ${w}`;
  }, [
    previewAspectRatio,
    previewResolution.height,
    previewResolution.width,
    previewTabloOrientation,
  ]);

  // Синхронно обновляем размер превью при смене разрешения или открытии секции, чтобы элементы масштабировались по размерам фона
  useLayoutEffect(() => {
    if (!sectionsExpanded.preview) return;
    const el = previewRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) {
        setPreviewSize({ width: w, height: h });
      }
    };
    measure();
    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [
    previewResolution.width,
    previewResolution.height,
    sectionsExpanded.preview,
  ]);

  const updateTabloSettings = (next: Partial<TerminalTabloSettings>) => {
    if (!form) return;
    setForm({
      ...form,
      tablo: {
        ...tabloSettings,
        ...next,
      },
    });
  };

  const updateTabloElement = (
    key: TabloElementKey,
    next: Partial<TerminalTabloElementSettings>,
  ) => {
    if (!form) return;
    setForm({
      ...form,
      tablo: {
        ...tabloSettings,
        elements: {
          ...tabloSettings.elements,
          [key]: {
            ...tabloSettings.elements[key],
            ...next,
          },
        },
      },
    });
  };

  const toggleElementExpanded = (key: TabloElementKey) => {
    setExpandedElements((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const updateTabloLogoSettings = (next: Partial<TerminalLogoSettings>) => {
    if (!form) return;
    setForm({
      ...form,
      tablo: {
        ...tabloSettings,
        logo: {
          url: tabloSettings.logo?.url ?? null,
          fitMode: tabloSettings.logo?.fitMode ?? defaultTabloLogoFitMode,
          scale: tabloSettings.logo?.scale ?? defaultTabloLogoScale,
          positionX: tabloSettings.logo?.positionX ?? defaultTabloLogoPosition,
          positionY: tabloSettings.logo?.positionY ?? defaultTabloLogoPosition,
          ...next,
        },
      },
    });
  };

  const handleUploadTabloLogo = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!canEditTablo) {
      alertContext?.setAlert("Недостаточно прав для редактирования настроек.");
      event.target.value = "";
      return;
    }
    const file = event.target.files?.[0];
    if (!file) return;
    uploadTabloLogo(file, {
      onSuccess: (res) => {
        updateTabloLogoSettings({ url: res.data.url });
      },
    });
    event.target.value = "";
  };

  const handleRemoveTabloLogo = () => {
    if (!canEditTablo) {
      alertContext?.setAlert("Недостаточно прав для редактирования настроек.");
      return;
    }
    updateTabloLogoSettings({ url: null });
  };

  const handleSaveClick = () => {
    if (!canEditTablo) {
      alertContext?.setAlert("Недостаточно прав для редактирования настроек.");
      return;
    }
    if (!form) return;
    setSaveModalBranchIds(filterBranchIds);
    setSaveModalQueueIds(filterQueueIds);
    setSaveBindingModalOpen(true);
  };

  const handleSaveBindingConfirm = async () => {
    if (!canEditTablo) {
      alertContext?.setAlert("Недостаточно прав для редактирования настроек.");
      return;
    }
    if (!form) return;
    const queueIdsToSave: number[] = [...saveModalQueueIds];
    const skippedQueueIds: number[] = [];

    // Проверяем ориентацию только для отмеченных очередей (не добавляем очереди "по филиалу" автоматически)
    if (queueIdsToSave.length > 0 && saveModalBranchIds.length > 0) {
      const queuesByBranch = await Promise.all(
        saveModalBranchIds.map((branchId) =>
          axios
            .get<
              BranchQueueItemDto[]
            >(BranchAPI.getQueues(branchId), commonHeaders())
            .then((r) => r.data),
        ),
      );
      const queuesIndex = new Map<number, BranchQueueItemDto>();
      queuesByBranch
        .flat()
        .forEach((q) => queuesIndex.set(q.id, q));

      const selectedOrientations = new Set<"VERTICAL" | "HORIZONTAL">();
      queueIdsToSave.forEach((id) => {
        const o = queuesIndex.get(id)?.tabloOrientation as
          | "VERTICAL"
          | "HORIZONTAL"
          | null
          | undefined;
        if (o) {
          selectedOrientations.add(o);
        }
      });
      if (selectedOrientations.size > 1) {
        alertContext?.setAlert(
          "Среди выбранных очередей есть очереди с разной ориентацией табло. Выберите очереди с одной ориентацией.",
        );
        return;
      }

      queueIdsToSave.forEach((id) => {
        const q = queuesIndex.get(id);
        if (
          q?.tabloOrientation &&
          q.tabloOrientation !== previewTabloOrientation
        ) {
          skippedQueueIds.push(id);
        }
      });
    }

    const allowedQueueIds = queueIdsToSave.filter(
      (id) => !skippedQueueIds.includes(id),
    );

    // Если для каких-то очередей известна ориентация табло и она не совпадает с превью — не применяем к ним настройки
    if (skippedQueueIds.length > 0) {
      alertContext?.setAlert(
        "Настройки табло не применены к части очередей: ориентация табло не совпадает с ориентацией превью.",
      );
    }

    if (allowedQueueIds.length === 0) {
      alertContext?.setAlert(
        "Выберите хотя бы одну очередь (с ориентацией, совпадающей с превью), чтобы применить настройки.",
      );
      return;
    }
    updateTerminalAppearance({
      ...form,
      videoVolume: normalizedVideoVolume,
      tablo: {
        ...tabloSettings,
        logo: {
          url: tabloLogo?.url ?? null,
          fitMode: tabloLogoFitMode,
          scale: tabloLogoScaleValue,
          positionX: tabloLogoPositionXValue,
          positionY: tabloLogoPositionYValue,
        },
      },
      queueIds: allowedQueueIds,
    });
    setSaveBindingModalOpen(false);
  };

  const percentToPx = (percent: number, size: number) => (percent / 100) * size;
  const pxToPercent = (px: number, size: number) =>
    Math.round(clampNumber(px / size, 0, 1) * 100);

  const snapThresholdPx = useMemo(() => {
    // Чуть адаптируем порог под размер превью, чтобы на маленьких экранах "прилипание" не было слишком жёстким
    return Math.max(4, Math.min(10, Math.round(previewSize.width / 200)));
  }, [previewSize.width]);

  const getElementRectPx = (key: TabloElementKey): RectPx => {
    const el = tabloSettings.elements[key];
    if (!el) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    return {
      x: percentToPx(el.x, previewSize.width),
      y: percentToPx(el.y, previewSize.height),
      width: percentToPx(el.width, previewSize.width),
      height: percentToPx(el.height, previewSize.height),
    };
  };

  const buildSnapLines = (
    activeKey: TabloElementKey,
    boundsW: number,
    boundsH: number,
  ): { x: number[]; y: number[] } => {
    const x = new Set<number>([0, Math.round(boundsW / 2), boundsW]);
    const y = new Set<number>([0, Math.round(boundsH / 2), boundsH]);

    (Object.keys(tabloSettings.elements) as TabloElementKey[]).forEach((k) => {
      if (k === activeKey) return;
      const el = tabloSettings.elements[k];
      if (!el.isEnabled) return;
      const r = getElementRectPx(k);
      x.add(Math.round(r.x));
      x.add(Math.round(r.x + r.width / 2));
      x.add(Math.round(r.x + r.width));
      y.add(Math.round(r.y));
      y.add(Math.round(r.y + r.height / 2));
      y.add(Math.round(r.y + r.height));
    });

    return {
      x: Array.from(x).sort((a, b) => a - b),
      y: Array.from(y).sort((a, b) => a - b),
    };
  };

  const snapRectToLines = (
    rect: RectPx,
    lines: { x: number[]; y: number[] },
    mode: "drag" | "resize",
    dir?: string,
  ): { rect: RectPx; guides: { x: number[]; y: number[] } } => {
    const r: RectPx = { ...rect };
    const activeGuides = { x: [] as number[], y: [] as number[] };

    const left = r.x;
    const cx = r.x + r.width / 2;
    const right = r.x + r.width;
    const top = r.y;
    const cy = r.y + r.height / 2;
    const bottom = r.y + r.height;

    const considerX = (edge: number, apply: (delta: number) => void) => {
      let best: { delta: number; line: number } | null = null;
      for (const line of lines.x) {
        const delta = line - edge;
        const ad = Math.abs(delta);
        if (ad <= snapThresholdPx && (!best || ad < Math.abs(best.delta))) {
          best = { delta, line };
        }
      }
      if (best) {
        apply(best.delta);
        activeGuides.x.push(best.line);
      }
    };

    const considerY = (edge: number, apply: (delta: number) => void) => {
      let best: { delta: number; line: number } | null = null;
      for (const line of lines.y) {
        const delta = line - edge;
        const ad = Math.abs(delta);
        if (ad <= snapThresholdPx && (!best || ad < Math.abs(best.delta))) {
          best = { delta, line };
        }
      }
      if (best) {
        apply(best.delta);
        activeGuides.y.push(best.line);
      }
    };

    if (mode === "drag") {
      considerX(left, (d) => (r.x += d));
      considerX(cx, (d) => (r.x += d));
      considerX(right, (d) => (r.x += d));
      considerY(top, (d) => (r.y += d));
      considerY(cy, (d) => (r.y += d));
      considerY(bottom, (d) => (r.y += d));
      return { rect: r, guides: activeGuides };
    }

    const d = dir ?? "";
    const affectsLeft = d.includes("left");
    const affectsRight = d.includes("right");
    const affectsTop = d.includes("top");
    const affectsBottom = d.includes("bottom");

    if (affectsLeft) {
      considerX(left, (delta) => {
        const nextX = r.x + delta;
        const nextW = r.width - delta;
        if (nextW > 0) {
          r.x = nextX;
          r.width = nextW;
        }
      });
    }
    if (affectsRight) {
      considerX(right, (delta) => {
        const nextW = r.width + delta;
        if (nextW > 0) r.width = nextW;
      });
    }
    if (affectsTop) {
      considerY(top, (delta) => {
        const nextY = r.y + delta;
        const nextH = r.height - delta;
        if (nextH > 0) {
          r.y = nextY;
          r.height = nextH;
        }
      });
    }
    if (affectsBottom) {
      considerY(bottom, (delta) => {
        const nextH = r.height + delta;
        if (nextH > 0) r.height = nextH;
      });
    }

    return { rect: r, guides: activeGuides };
  };

  const previewBorderRadiusPx = Math.max(
    4,
    Math.round(30 * (previewSize.width / previewResolution.width)),
  );

  const buildPreviewStyle = (
    element: TerminalTabloElementSettings,
  ): CSSProperties => {
    const blur = Number.isFinite(element.backdropBlur)
      ? Number(element.backdropBlur)
      : null;
    return {
      backgroundColor: element.backgroundColor || undefined,
      backdropFilter: blur !== null ? `blur(${blur}px)` : undefined,
      WebkitBackdropFilter: blur !== null ? `blur(${blur}px)` : undefined,
      borderRadius: `${previewBorderRadiusPx}px`,
    };
  };

  const toPreviewSize = (value: number | null | undefined, axis: "x" | "y") => {
    if (!Number.isFinite(value)) {
      return undefined;
    }
    const size = Number(value);
    const scale =
      axis === "x"
        ? previewSize.width / previewResolution.width
        : previewSize.height / previewResolution.height;
    return `${Math.max(8, Math.round(size * scale))}px`;
  };
  /** Масштабирует размер шрифта (px на разрешении табло) под превью */
  const toPreviewFontSize = (
    px: number | null | undefined,
  ): string | undefined => {
    if (!Number.isFinite(px) || px <= 0) return undefined;
    return toPreviewSize(Number(px), "x");
  };
  const buildTextStyle = (
    element: TerminalTabloElementSettings,
  ): CSSProperties => ({
    fontFamily: element.fontFamily || undefined,
    fontWeight: element.fontWeight || undefined,
    fontSize: toPreviewFontSize(element.fontSize),
    color: element.textColor || undefined,
  });
  /** Масштабирует line-height (px на разрешении табло) под превью */
  const toPreviewLineHeight = (
    px: number | null | undefined,
  ): string | undefined => {
    if (!Number.isFinite(px) || px <= 0) return undefined;
    return toPreviewSize(Number(px), "y");
  };
  const buildPreviewTicketBoxStyle = (
    element: TerminalTabloElementSettings,
    background: string,
  ): CSSProperties => {
    const width = toPreviewSize(element.numberWidth, "x");
    const height = toPreviewSize(element.numberHeight, "y");
    const horizontalPadding = toPreviewSize(30, "x") ?? "4px";
    const verticalPadding = toPreviewSize(10, "y") ?? "2px";

    return {
      width,
      minWidth: 0,
      maxWidth: "100%",
      height,
      minHeight: height,
      maxHeight: height,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: `${verticalPadding} ${horizontalPadding}`,
      boxSizing: "border-box",
      borderRadius: toPreviewSize(20, "x") ?? "6px",
      background,
    };
  };

  const renderPreviewElement = (
    key: TabloElementKey,
    content: React.ReactNode,
    contentAlign: "start" | "center" = "center",
  ): React.ReactNode => {
    const element = tabloSettings.elements[key];
    if (!element) return null;
    if (!element.isEnabled) {
      return null;
    }
    const draft = previewDraftRects[key];
    const baseRect = getElementRectPx(key);
    const rect = draft ?? baseRect;
    const widthPx = rect.width;
    const heightPx = rect.height;
    const xPx = rect.x;
    const yPx = rect.y;
    const contentClassName =
      contentAlign === "start"
        ? `${classes.previewItemContent} ${classes.previewContentStart}`
        : classes.previewItemContent;
    return (
      <Rnd
        key={`${key}-${previewSize.width}-${previewSize.height}`}
        bounds="parent"
        disableDragging={!canEditTablo}
        enableResizing={canEditTablo}
        size={{ width: widthPx, height: heightPx }}
        position={{ x: xPx, y: yPx }}
        minWidth={Math.max(40, percentToPx(2, previewSize.width))}
        minHeight={Math.max(24, percentToPx(2, previewSize.height))}
        onDragStart={() => {
          const boundsEl = previewRef.current;
          void boundsEl;
          // Направляющие показываем только когда реально "поймали" линию
          setPreviewGuides(null);
          setPreviewDraftRects((prev) => ({
            ...prev,
            [key]: getElementRectPx(key),
          }));
        }}
        onDrag={(_, data) => {
          if (!data) return;
          const boundsEl = previewRef.current;
          const boundsW = boundsEl?.clientWidth ?? previewSize.width;
          const boundsH = boundsEl?.clientHeight ?? previewSize.height;
          const lines = buildSnapLines(key, boundsW, boundsH);
          const nextRect: RectPx = {
            x: data.x,
            y: data.y,
            width: rect.width,
            height: rect.height,
          };
          const snapped = snapRectToLines(nextRect, lines, "drag");
          setPreviewDraftRects((prev) => ({ ...prev, [key]: snapped.rect }));
          setPreviewGuides(snapped.guides ?? { x: [], y: [] });
        }}
        onDragStop={(_, data) => {
          if (!data) return;
          const boundsEl = previewRef.current;
          const boundsW = boundsEl?.clientWidth ?? previewSize.width;
          const boundsH = boundsEl?.clientHeight ?? previewSize.height;
          const lines = buildSnapLines(key, boundsW, boundsH);
          const nextRect: RectPx = {
            x: data.x,
            y: data.y,
            width: rect.width,
            height: rect.height,
          };
          const snapped = snapRectToLines(nextRect, lines, "drag");
          updateTabloElement(key, {
            x: pxToPercent(snapped.rect.x, boundsW),
            y: pxToPercent(snapped.rect.y, boundsH),
          });
          setPreviewDraftRects((prev) => {
            const { [key]: _, ...rest } = prev;
            return rest;
          });
          setPreviewGuides(null);
        }}
        onResizeStart={(_, dir, ref, __, position) => {
          const boundsEl = previewRef.current;
          void boundsEl;
          void dir;
          if (!position) return;
          setPreviewGuides(null);
          setPreviewDraftRects((prev) => ({
            ...prev,
            [key]: {
              x: position.x,
              y: position.y,
              width: ref.offsetWidth,
              height: ref.offsetHeight,
            },
          }));
        }}
        onResize={(_, dir, ref, __, position) => {
          if (!position) return;
          const boundsEl = previewRef.current;
          const boundsW = boundsEl?.clientWidth ?? previewSize.width;
          const boundsH = boundsEl?.clientHeight ?? previewSize.height;
          const lines = buildSnapLines(key, boundsW, boundsH);
          const nextRect: RectPx = {
            x: position.x,
            y: position.y,
            width: ref.offsetWidth,
            height: ref.offsetHeight,
          };
          const snapped = snapRectToLines(nextRect, lines, "resize", dir);
          setPreviewDraftRects((prev) => ({ ...prev, [key]: snapped.rect }));
          setPreviewGuides(snapped.guides ?? { x: [], y: [] });
        }}
        onResizeStop={(_, dir, ref, ___, position) => {
          if (!position) return;
          const boundsEl = previewRef.current;
          const boundsW = boundsEl?.clientWidth ?? previewSize.width;
          const boundsH = boundsEl?.clientHeight ?? previewSize.height;
          const lines = buildSnapLines(key, boundsW, boundsH);
          const nextRect: RectPx = {
            x: position.x,
            y: position.y,
            width: ref.offsetWidth,
            height: ref.offsetHeight,
          };
          const snapped = snapRectToLines(nextRect, lines, "resize", dir);
          updateTabloElement(key, {
            width: pxToPercent(snapped.rect.width, boundsW),
            height: pxToPercent(snapped.rect.height, boundsH),
            x: pxToPercent(snapped.rect.x, boundsW),
            y: pxToPercent(snapped.rect.y, boundsH),
          });
          setPreviewDraftRects((prev) => {
            const { [key]: _, ...rest } = prev;
            return rest;
          });
          setPreviewGuides(null);
        }}
        className={classes.previewItem}
        style={{ opacity: 1 }}
      >
        <div className={contentClassName} style={buildPreviewStyle(element)}>
          {content}
          <div className={classes.previewItemPosition}>
            {element.x.toFixed(0)}%, {element.y.toFixed(0)}% ·{" "}
            {element.width.toFixed(0)}%×
            {element.height.toFixed(0)}%
          </div>
        </div>
      </Rnd>
    );
  };

  const backgroundMedia = useMemo(() => {
    if (!tabloSettings.backgroundId) return null;
    return (
      backgrounds.find((item) => item.id === tabloSettings.backgroundId) ?? null
    );
  }, [backgrounds, tabloSettings.backgroundId]);

  const handleUploadBackground = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!event.target.files?.length) return;
    uploadTerminalBackground(event.target.files[0]);
    event.target.value = "";
  };

  const handleDeleteBackground = (backgroundId: number) => {
    if (tabloSettings.backgroundId === backgroundId) {
      updateTabloSettings({ backgroundId: null });
    }
    deleteTerminalBackground(backgroundId);
  };

  const backgroundFit = tabloSettings.backgroundFitMode ?? "cover";
  const backgroundObjectFit =
    backgroundFit === "stretch"
      ? "fill"
      : backgroundFit === "contain" || backgroundFit === "cover"
        ? backgroundFit
        : backgroundFit === "none"
          ? "none"
          : "contain";

  const toggleSectionExpanded = (key: TabloSectionKey) => {
    setSectionsExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!hasSelectedQueue) {
    return (
      <div className={classes.container}>
        <ContentHeader
          name="Табло"
          filterContent={
            <BranchQueueFilter
              selectedBranchIds={filterBranchIds}
              selectedQueueIds={filterQueueIds}
              onBranchChange={setFilterBranchIds}
              onQueueChange={setFilterQueueIds}
              singleQueue
              queueIdsFilter={queueIdsFilter}
            />
          }
        />
        <div className={classes.section}>
          <p className={classes.noQueueSelectedHint}>
            Выберите очередь в фильтре, чтобы настроить табло.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <ContentHeader
        name="Табло"
        filterContent={
          <BranchQueueFilter
            selectedBranchIds={filterBranchIds}
            selectedQueueIds={filterQueueIds}
            onBranchChange={setFilterBranchIds}
            onQueueChange={setFilterQueueIds}
            singleQueue
            queueIdsFilter={queueIdsFilter}
          />
        }
      />
      {saveBindingModalOpen && (
        <div
          className={classNames(classes.saveBindingModal, {
            [classes.saveBindingModalActive]: saveBindingModalOpen,
          })}
          onClick={() => setSaveBindingModalOpen(false)}
        >
          <div
            className={classes.saveBindingModalContainer}
            onClick={(e) => e.stopPropagation()}
          >
            {/* <h2 className={classes.saveBindingModalTitle}>
              Привязка к филиалам и очередям
            </h2> */}
            <BranchQueueTreeSelect
              label=""
              selectedBranchIds={saveModalBranchIds}
              selectedQueueIds={saveModalQueueIds}
              onBranchChange={setSaveModalBranchIds}
              onQueueChange={setSaveModalQueueIds}
              className={classes.saveBindingTreeSelect}
            />
            <div className={classes.saveBindingModalActions}>
              <Button
                content="Отмена"
                btnStyle="white"
                onClick={() => setSaveBindingModalOpen(false)}
              />
              <Button
                content="Применить"
                btnStyle="blue"
                onClick={handleSaveBindingConfirm}
                isLoading={isUpdateTerminalAppearanceLoading}
                isDisabled={!canEditTablo || saveModalQueueIds.length === 0}
              />
            </div>
          </div>
        </div>
      )}
      {isLoadingTerminalAppearance && <div>Загрузка...</div>}
      {form && (
        <div
          className={classNames(classes.form, {
            [classes.readOnly]: !canEditTablo,
          })}
          aria-disabled={!canEditTablo}
        >
          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("preview")}
            >
              <h3>Превью табло</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.preview ? classes.collapseIconExpanded : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.preview ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.preview && (
              <>
                <div className={classes.previewResolution}>
                  {/* <label>
                    Ширина (px)
                    <input
                      type="number"
                      min={320}
                      max={7680}
                      step={1}
                      value={resolutionWidthInput}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setResolutionWidthInput(raw);
                        const n = parseInt(raw, 10);
                        if (Number.isFinite(n)) {
                          const clamped = Math.max(320, Math.min(7680, n));
                          updateTabloSettings({ resolutionWidth: clamped });
                        }
                      }}
                      onBlur={() => {
                        const n = parseInt(resolutionWidthInput, 10);
                        if (!Number.isFinite(n)) {
                          setResolutionWidthInput(String(resolutionWidth));
                          return;
                        }
                        const clamped = Math.max(320, Math.min(7680, n));
                        updateTabloSettings({ resolutionWidth: clamped });
                        setResolutionWidthInput(String(clamped));
                      }}
                    />
                  </label>
                  <label>
                    Высота (px)
                    <input
                      type="number"
                      min={240}
                      max={4320}
                      step={1}
                      value={resolutionHeightInput}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setResolutionHeightInput(raw);
                        const n = parseInt(raw, 10);
                        if (Number.isFinite(n)) {
                          const clamped = Math.max(240, Math.min(4320, n));
                          updateTabloSettings({ resolutionHeight: clamped });
                        }
                      }}
                      onBlur={() => {
                        const n = parseInt(resolutionHeightInput, 10);
                        if (!Number.isFinite(n)) {
                          setResolutionHeightInput(String(resolutionHeight));
                          return;
                        }
                        const clamped = Math.max(240, Math.min(4320, n));
                        updateTabloSettings({ resolutionHeight: clamped });
                        setResolutionHeightInput(String(clamped));
                      }}
                    />
                  </label> */}
                  <label>
                    Ориентация табло
                    <select
                      value={previewTabloOrientation}
                      onChange={(e) => {
                        previewOrientationTouchedRef.current = true;
                        setPreviewTabloOrientation(
                          (e.target.value as "VERTICAL" | "HORIZONTAL") ||
                            "HORIZONTAL",
                        );
                      }}
                    >
                      <option value="HORIZONTAL">Горизонтальная</option>
                      <option value="VERTICAL">Вертикальная</option>
                    </select>
                  </label>
                  <label>
                    Соотношение сторон
                    <select
                      value={previewAspectRatio}
                      onChange={(e) =>
                        setPreviewAspectRatio(
                          (e.target.value as "16:9" | "4:3") || "16:9",
                        )
                      }
                    >
                      <option value="16:9">16:9</option>
                      <option value="4:3">4:3</option>
                    </select>
                  </label>
                  <span className={classes.previewResolutionHint}>
                    Разрешение реального табло. Перетаскивайте и меняйте размер
                    элементов в превью.
                  </span>
                </div>
                <div
                  className={classNames(classes.tabloPreviewFrameWrap, {
                    [classes.tabloPreviewFrameWrapVertical]:
                      previewTabloOrientation === "VERTICAL",
                    [classes.tabloPreviewFrameWrapHorizontal4x3]:
                      previewTabloOrientation === "HORIZONTAL" &&
                      previewAspectRatio === "4:3",
                    [classes.tabloPreviewFrameWrapVertical3x4]:
                      previewTabloOrientation === "VERTICAL" &&
                      previewAspectRatio === "4:3",
                  })}
                  ref={previewRef}
                  style={{
                    aspectRatio: previewAspectRatioValue,
                    ["--tablo-preview-ratio" as string]: String(
                      previewResolution.width / previewResolution.height,
                    ),
                  }}
                >
                  <div
                    className={classes.tabloPreviewFrame}
                    style={{
                      background: tabloSettings.backgroundColor ?? "#0b0f1a",
                    }}
                  >
                    {backgroundMedia && (
                      <div className={classes.previewBackground}>
                        {backgroundMedia.mediaType === "VIDEO" ? (
                          <video
                            className={classes.previewBackgroundMedia}
                            style={{ objectFit: backgroundObjectFit }}
                            autoPlay
                            muted
                            loop
                            playsInline
                            src={backgroundMedia.url}
                          />
                        ) : (
                          <img
                            className={classes.previewBackgroundMedia}
                            style={{ objectFit: backgroundObjectFit }}
                            src={backgroundMedia.url}
                            alt=""
                          />
                        )}
                      </div>
                    )}
                    <div className={classes.previewOverlay} />
                    {((previewGuides?.x?.length ?? 0) > 0 ||
                      (previewGuides?.y?.length ?? 0) > 0) && (
                      <div className={classes.previewGuides}>
                        {(previewGuides?.x ?? []).map((x) => (
                          <div
                            key={`gx-${x}`}
                            className={classes.previewGuideX}
                            style={{ left: `${x}px` }}
                          />
                        ))}
                        {(previewGuides?.y ?? []).map((y) => (
                          <div
                            key={`gy-${y}`}
                            className={classes.previewGuideY}
                            style={{ top: `${y}px` }}
                          />
                        ))}
                      </div>
                    )}
                    {renderPreviewElement(
                    "header",
                    (() => {
                      const headerEl = tabloSettings.elements.header;
                      const headerTextStyle = buildTextStyle(headerEl);
                      const headerPreviewFontSize =
                        headerTextStyle.fontSize ??
                        toPreviewFontSize(
                          headerEl.fontSize ??
                            defaultTabloSettings.elements.header.fontSize,
                        );
                      const headerIconColor =
                        typeof headerTextStyle.color === "string"
                          ? headerTextStyle.color
                          : undefined;
                      return (
                        <div
                          className={classes.previewHeader}
                          style={
                            headerPreviewFontSize
                              ? { fontSize: headerPreviewFontSize }
                              : undefined
                          }
                        >
                          <div
                            className={classes.previewHeaderLogo}
                            style={{
                              width: `${(145 / previewResolution.width) * 100}%`,
                              minWidth: "unset",
                              maxWidth: "unset",
                              backgroundImage: `url(${tabloLogoPreview})`,
                              backgroundSize: getLogoBackgroundSize(
                                tabloLogoFitMode,
                                tabloLogoScaleValue,
                              ),
                              backgroundPosition: `${tabloLogoPositionXValue}% ${tabloLogoPositionYValue}%`,
                            }}
                            aria-hidden
                          />
                          <div className={classes.previewDateTimeContainer}>
                            <div
                              className={classes.previewTimeSectionComponent}
                            >
                              <HeaderCalendarIcon color={headerIconColor} />
                              <span style={headerTextStyle}>
                                {previewDateTime.date}
                              </span>
                            </div>
                            <div
                              className={classes.previewTimeSectionComponent}
                            >
                              <HeaderClockIcon color={headerIconColor} />
                              <span style={headerTextStyle}>
                                {previewDateTime.time}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })(),
                    )}
                    {renderPreviewElement(
                    "video",
                    (() => {
                      const ids =
                        tabloSettings.elements.video.videoPlaylistIds ?? [];
                      const urls = tabloSettings.elements.video.videoUrls ?? [];
                      const parts = [];
                      if (urls.length > 0)
                        parts.push(`${urls.length} загружено`);
                      if (ids.length > 0)
                        parts.push(`${ids.length} в плейлисте`);
                      return (
                        <div className={classes.previewVideo}>
                          Видео
                          {parts.length > 0 && (
                            <span className={classes.previewVideoCount}>
                              ({parts.join(", ")})
                            </span>
                          )}
                        </div>
                      );
                    })(),
                  )}
                  {renderPreviewElement(
                    "title",
                    (() => {
                      const element = tabloSettings.elements.title;
                      const titleFontSize =
                        element.titleFontSize ?? element.fontSize;
                      const subTitleFontSize =
                        element.subTitleFontSize ??
                        (element.fontSize
                          ? Math.max(10, Math.round(element.fontSize * 0.65))
                          : undefined);
                      const titleLineHeightValue =
                        element.titleLineHeight != null &&
                        Number.isFinite(element.titleLineHeight)
                          ? (toPreviewLineHeight(element.titleLineHeight) ??
                            `${element.titleLineHeight}px`)
                          : undefined;
                      const subTitleLineHeightValue =
                        element.subTitleLineHeight != null &&
                        Number.isFinite(element.subTitleLineHeight)
                          ? (toPreviewLineHeight(element.subTitleLineHeight) ??
                            `${element.subTitleLineHeight}px`)
                          : undefined;
                      const titleStyle: CSSProperties = {
                        fontFamily:
                          element.titleFontFamily ??
                          element.fontFamily ??
                          undefined,
                        fontWeight:
                          element.titleFontWeight ??
                          element.fontWeight ??
                          undefined,
                        fontSize: toPreviewFontSize(titleFontSize),
                        color:
                          element.titleTextColor ??
                          element.textColor ??
                          undefined,
                        lineHeight: titleLineHeightValue,
                      };
                      const subTitleStyle: CSSProperties = {
                        fontFamily:
                          element.subTitleFontFamily ??
                          element.fontFamily ??
                          undefined,
                        fontWeight:
                          element.subTitleFontWeight ??
                          element.fontWeight ??
                          undefined,
                        fontSize: toPreviewFontSize(subTitleFontSize),
                        color:
                          element.subTitleTextColor ??
                          element.textColor ??
                          undefined,
                        lineHeight: subTitleLineHeightValue,
                      };
                      return (
                        <div className={classes.previewTitle}>
                          <div style={titleStyle}>
                            {element.titleText?.trim() || "Заголовок табло"}
                          </div>
                          <div
                            className={classes.previewSubTitle}
                            style={subTitleStyle}
                          >
                            {element.subTitleText?.trim() ||
                              "Подзаголовок табло"}
                          </div>
                        </div>
                      );
                    })(),
                    "start",
                  )}
                  {renderPreviewElement(
                    "queue",
                    (() => {
                      const queueEl = tabloSettings.elements.queue;
                      const queueHeaderFontSize = toPreviewFontSize(28);
                      const ticketFontSize = toPreviewFontSize(
                        queueEl.fontSize
                          ? Math.max(12, Math.round(queueEl.fontSize * 1.1))
                          : null,
                      );
                      const windowFontSize = toPreviewFontSize(
                        queueEl.fontSize
                          ? Math.max(12, Math.round(queueEl.fontSize * 1))
                          : null,
                      );
                      const queueHeaderStyle: CSSProperties = {
                        ...buildTextStyle(queueEl),
                        ...(queueHeaderFontSize
                          ? { fontSize: queueHeaderFontSize }
                          : {}),
                      };
                      const queueRowStyle: CSSProperties = {
                        ...buildTextStyle(queueEl),
                        ...(ticketFontSize ? { fontSize: ticketFontSize } : {}),
                      };
                      const windowStyle: CSSProperties = {
                        ...buildTextStyle(queueEl),
                        ...(windowFontSize ? { fontSize: windowFontSize } : {}),
                      };
                      const renderQueueHeader = () => (
                        <div className={classes.previewQueueHeader}>
                          <div
                            className={classes.previewQueueHeaderTitlesWrapper}
                          >
                            <div className={classes.previewQueueHeaderTitle}>
                              <QueueTicketIcon
                                color={queueEl.textColor ?? undefined}
                              />
                              <span style={queueHeaderStyle}>Талон</span>
                            </div>
                            <div className={classes.previewQueueHeaderTitle}>
                              <QueueWindowIcon
                                color={queueEl.textColor ?? undefined}
                              />
                              <span style={queueHeaderStyle}>Окно</span>
                            </div>
                          </div>
                        </div>
                      );
                      const renderQueueRow = (
                        ticketText: string,
                        windowText: string,
                        ticketBg: string,
                      ) => (
                        <div
                          className={classes.previewItemQueue}
                          style={queueRowStyle}
                        >
                          <div
                            className={classes.previewTicketBox}
                            style={buildPreviewTicketBoxStyle(
                              queueEl,
                              ticketBg,
                            )}
                          >
                            <span>{ticketText}</span>
                          </div>
                          <QueueArrowIcon />
                          <div className={classes.previewPost}>
                            <span style={windowStyle}>{windowText}</span>
                          </div>
                        </div>
                      );
                      const previewTicketBlinkBG =
                        queueEl.ticketBlinkBGColor ?? "rgba(76, 175, 80, 0.6)";
                      const previewTicketBG =
                        queueEl.ticketBGColor ?? "#0066ff";
                      return tabloSettings.elements.queue.queueTwoColumns ? (
                        <div className={classes.previewQueueTwoColumns}>
                          <div className={classes.previewQueueColumn}>
                            {renderQueueHeader()}
                            <div className={classes.previewQueueBody}>
                              {renderQueueRow(
                                "A12",
                                "Кабинет 1 Окно 1",
                                previewTicketBlinkBG,
                              )}
                            </div>
                          </div>
                          <div className={classes.previewQueueColumn}>
                            {renderQueueHeader()}
                            <div className={classes.previewQueueBody}>
                              {renderQueueRow("B55", "Окно 3", previewTicketBG)}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className={classes.previewQueue}>
                          {renderQueueHeader()}
                          <div className={classes.previewQueueBody}>
                            {renderQueueRow(
                              "A12",
                              "Кабинет 1 Окно 1",
                              previewTicketBlinkBG,
                            )}
                            {renderQueueRow("B55", "Окно 3", previewTicketBG)}
                          </div>
                        </div>
                      );
                    })(),
                    "start",
                  )}
                  {renderPreviewElement(
                    "takenTickets",
                    (() => {
                      const takenEl = tabloSettings.elements.takenTickets;
                      const takenHeaderFontSize = toPreviewFontSize(28);
                      const takenItemFontSize = toPreviewFontSize(
                        takenEl.fontSize
                          ? Math.max(12, Math.round(takenEl.fontSize))
                          : null,
                      );
                      const takenHeaderStyle: CSSProperties = {
                        ...buildTextStyle(takenEl),
                        ...(takenHeaderFontSize
                          ? { fontSize: takenHeaderFontSize }
                          : {}),
                      };
                      const ticketItems = [
                        "A102",
                        "Y008",
                        "J191",
                        "F013",
                        "H073",
                        "S735",
                        "P236",
                        "C029",
                      ];
                      return (
                        <div className={classes.previewTakenTickets}>
                          <div className={classes.previewTakenTicketsHeader}>
                            <div
                              className={classes.previewTakenTicketsHeaderTitle}
                            >
                              <QueueTicketClook
                                color={takenEl.textColor ?? undefined}
                              />
                              <span style={takenHeaderStyle}>Ожидание</span>
                            </div>
                          </div>
                          <div className={classes.previewTakenTicketsList}>
                            {ticketItems.map((ticket, index) => (
                              <div
                                key={ticket}
                                className={
                                  index === 0
                                    ? `${classes.previewTakenTicketItem} ${classes.previewTakenTicketItemActive}`
                                    : classes.previewTakenTicketItem
                                }
                                style={{
                                  ...buildTextStyle(takenEl),
                                  ...(takenItemFontSize
                                    ? { fontSize: takenItemFontSize }
                                    : {}),
                                  ...buildPreviewTicketBoxStyle(
                                    takenEl,
                                    index === 0
                                      ? "rgba(255, 255, 255, 0.35)"
                                      : "rgba(255, 255, 255, 0.18)",
                                  ),
                                }}
                              >
                                {ticket}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })(),
                    "start",
                  )}
                  {renderPreviewElement(
                    "ticker",
                    (() => {
                      const tickerEl = tabloSettings.elements.ticker;
                      const tickerSpeedSeconds =
                        form?.tickerSpeed != null &&
                        Number.isFinite(form.tickerSpeed)
                          ? Number(form.tickerSpeed)
                          : defaultTickerSpeedSeconds;
                      const tickerText =
                        (tickerEl.tickerTexts ?? []).filter((t) => t?.trim())
                          .length > 0
                          ? (tickerEl.tickerTexts ?? [])
                              .filter((t) => t?.trim())
                              .join(" • ")
                          : "Бегущая строка: новости и важные сообщения";
                      return (
                        <div className={classes.previewTicker}>
                          <span
                            className={classes.previewTickerInner}
                            style={{
                              ...buildTextStyle(tickerEl),
                              ["--ticker-animation-duration" as string]: `${tickerSpeedSeconds}s`,
                            }}
                          >
                            {tickerText}
                          </span>
                        </div>
                      );
                    })(),
                  )}
                  {renderPreviewElement(
                    "slider",
                    (() => {
                      const sliderEl = tabloSettings.elements.slider;
                      const urls = sliderEl.imageUrls ?? [];
                      const fitMode = sliderEl.fitMode ?? "contain";
                      const objectFit: React.CSSProperties["objectFit"] =
                        fitMode === "stretch"
                          ? "fill"
                          : fitMode === "none"
                            ? "none"
                            : fitMode === "scale"
                              ? "scale-down"
                              : fitMode === "width" || fitMode === "height"
                                ? "contain"
                                : fitMode;
                      if (urls.length > 0) {
                        return (
                          <div className={classes.previewSliderInner}>
                            <img
                              src={normalizeMediaUrl(urls[0])}
                              alt=""
                              draggable={false}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit,
                                pointerEvents: "none",
                              }}
                            />
                          </div>
                        );
                      }
                      return (
                        <div className={classes.previewVideo}>Слайдер</div>
                      );
                    })(),
                  )}
                  {renderPreviewElement(
                    "weatherWidget",
                    (() => {
                      const weatherEl = tabloSettings.elements.weatherWidget;
                      const region =
                        weatherEl.weatherRegion?.trim() || "Регион";
                      const baseTextStyle = buildTextStyle(weatherEl);
                      const regionFontSizePx = Math.min(
                        72,
                        Math.max(8, weatherEl.weatherRegionFontSize ?? 14),
                      );
                      const tempFontSizePx = Math.min(
                        96,
                        Math.max(14, weatherEl.weatherTempFontSize ?? 28),
                      );
                      const descFontSizePx = Math.min(
                        48,
                        Math.max(8, weatherEl.weatherDescriptionFontSize ?? 12),
                      );
                      const iconSizePx = Math.min(
                        200,
                        Math.max(16, weatherEl.weatherIconSize ?? 40),
                      );
                      const locationStyle: CSSProperties = {
                        ...baseTextStyle,
                        fontSize: toPreviewFontSize(regionFontSizePx),
                      };
                      const tempStyle: CSSProperties = {
                        ...baseTextStyle,
                        fontSize: toPreviewFontSize(tempFontSizePx),
                      };
                      const descStyle: CSSProperties = {
                        ...baseTextStyle,
                        fontSize: toPreviewFontSize(descFontSizePx),
                      };
                      return (
                        <div className={classes.previewWeatherWidget}>
                          <span
                            className={classes.previewWeatherWidgetLocation}
                            style={locationStyle}
                          >
                            📍 {region}
                          </span>
                          <div className={classes.previewWeatherWidgetTempRow}>
                            <span
                              className={classes.previewWeatherWidgetTemp}
                              style={tempStyle}
                            >
                              — °C
                            </span>
                            {iconSizePx > 0 && (
                              <span
                                className={classes.previewWeatherWidgetIcon}
                                style={{
                                  width: toPreviewSize(iconSizePx, "x"),
                                  height: toPreviewSize(iconSizePx, "y"),
                                  display: "inline-block",
                                  background: "rgba(255,255,255,0.2)",
                                  borderRadius: "4px",
                                }}
                                aria-hidden
                              />
                            )}
                          </div>
                          <span
                            className={classes.previewWeatherWidgetDesc}
                            style={descStyle}
                          >
                            Погода
                          </span>
                        </div>
                      );
                    })(),
                    "start",
                  )}
                  {renderPreviewElement(
                    "rssBeltaWidget",
                    (() => {
                      const rssEl = tabloSettings.elements.rssBeltaWidget;
                      const baseTextStyle = buildTextStyle(rssEl);
                      const titleFontSizePx = Math.min(
                        48,
                        Math.max(10, rssEl.fontSize ?? 16),
                      );
                      const metaFontSizePx = Math.max(
                        10,
                        Math.round(titleFontSizePx * 0.7),
                      );
                      const metaStyle: CSSProperties = {
                        ...baseTextStyle,
                        fontSize: toPreviewFontSize(metaFontSizePx),
                      };
                      const titleStyle: CSSProperties = {
                        ...baseTextStyle,
                        fontSize: toPreviewFontSize(titleFontSizePx),
                      };
                      return (
                        <div className={classes.previewRssBeltaWidget}>
                          <div className={classes.previewRssBeltaHeader}>
                            <span
                              className={classes.previewRssBeltaSource}
                              style={metaStyle}
                            >
                              БЕЛТА
                            </span>
                            <span
                              className={classes.previewRssBeltaCounter}
                              style={metaStyle}
                            >
                              1/{rssEl.rssNewsCount ?? 5}
                            </span>
                          </div>
                          <span
                            className={classes.previewRssBeltaCategory}
                            style={metaStyle}
                          >
                            Новости
                          </span>
                          <span
                            className={classes.previewRssBeltaTitle}
                            style={titleStyle}
                          >
                            RSS лента БЕЛТА
                          </span>
                        </div>
                      );
                    })(),
                    "start",
                  )}
                </div>
              </div>
              </>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("logo")}
            >
              <h3>Лого табло (jpg, jpeg, png, webp, gif, svg)</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.logo ? classes.collapseIconExpanded : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.logo ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.logo && (
              <div className={classes.buttonMediaUpload}>
                <div className={classes.logoUploadRow}>
                  <label htmlFor="tablo-logo-upload">
                    Изображение логотипа для табло
                  </label>
                  <div className={classes.logoUploadInputRow}>
                    <input
                      id="tablo-logo-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                      onChange={handleUploadTabloLogo}
                    />
                    {tabloLogoPreview && (
                      <CustomButton
                        content="Удалить"
                        styleType="default"
                        onClick={handleRemoveTabloLogo}
                      />
                    )}
                  </div>
                </div>
                {isUploadTabloLogoLoading && <span>Загрузка...</span>}
                <div className={classes.buttonMediaControls}>
                  <label>
                    Режим отображения
                    <select
                      className={classes.displayModeSelect}
                      value={tabloLogoFitMode}
                      onChange={(e) =>
                        updateTabloLogoSettings({
                          fitMode: e.target
                            .value as TerminalLogoSettings["fitMode"],
                        })
                      }
                    >
                      <option value="scale">Свой размер</option>
                      <option value="contain">Вписать в область</option>
                      <option value="cover">Заполнить область</option>
                      <option value="stretch">
                        Растянуть (по ширине и высоте)
                      </option>
                      <option value="width">Растянуть по ширине</option>
                      <option value="height">Растянуть по высоте</option>
                      <option value="none">Оригинальный размер</option>
                    </select>
                  </label>
                  <label className={classes.rangeField}>
                    Масштаб
                    <div className={classes.rangeRow}>
                      <input
                        type="range"
                        min={50}
                        max={200}
                        step={1}
                        value={tabloLogoScaleValue}
                        onChange={(e) =>
                          updateTabloLogoSettings({
                            scale: Number(e.target.value),
                          })
                        }
                        disabled={tabloLogoFitMode !== "scale"}
                      />
                      <input
                        type="number"
                        min={50}
                        max={200}
                        value={tabloLogoScaleInputValue}
                        onChange={(e) =>
                          updateTabloLogoSettings({
                            scale:
                              parseNumberInput(e.target.value) ??
                              defaultTabloLogoScale,
                          })
                        }
                        disabled={tabloLogoFitMode !== "scale"}
                      />
                      <span>%</span>
                    </div>
                  </label>
                  <label className={classes.rangeField}>
                    Позиция по X
                    <div className={classes.rangeRow}>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={tabloLogoPositionXValue}
                        onChange={(e) =>
                          updateTabloLogoSettings({
                            positionX: Number(e.target.value),
                          })
                        }
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={tabloLogoPositionXInputValue}
                        onChange={(e) =>
                          updateTabloLogoSettings({
                            positionX:
                              parseNumberInput(e.target.value) ??
                              defaultTabloLogoPosition,
                          })
                        }
                      />
                      <span>%</span>
                    </div>
                  </label>
                  <label className={classes.rangeField}>
                    Позиция по Y
                    <div className={classes.rangeRow}>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={tabloLogoPositionYValue}
                        onChange={(e) =>
                          updateTabloLogoSettings({
                            positionY: Number(e.target.value),
                          })
                        }
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={tabloLogoPositionYInputValue}
                        onChange={(e) =>
                          updateTabloLogoSettings({
                            positionY:
                              parseNumberInput(e.target.value) ??
                              defaultTabloLogoPosition,
                          })
                        }
                      />
                      <span>%</span>
                    </div>
                  </label>
                </div>
                <div className={classes.logoMediaCard}>
                  <div className={classes.logoMediaPreview}>
                    <div
                      className={classes.logoMediaPreviewImage}
                      style={{
                        backgroundImage: `url(${tabloLogoPreview})`,
                        backgroundSize: getLogoBackgroundSize(
                          tabloLogoFitMode,
                          tabloLogoScaleValue,
                        ),
                        backgroundPosition: `${tabloLogoPositionXValue}% ${tabloLogoPositionYValue}%`,
                      }}
                      aria-label="Предпросмотр логотипа табло"
                    />
                  </div>
                </div>
              </div>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("background")}
            >
              <h3>Фон табло (jpg, jpeg, png, webp, gif, svg, mp4, webm)</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.background
                    ? classes.collapseIconExpanded
                    : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.background ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.background && (
              <>
                <div className={classes.buttonMediaUpload}>
                  <label>
                    Изображение фона
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,video/mp4,video/webm,video/ogg,video/x-matroska"
                      onChange={handleUploadBackground}
                    />
                    {isUploadTerminalBackgroundLoading && (
                      <span>Загрузка...</span>
                    )}
                  </label>
                </div>
                <div className={classes.buttonMediaControls}>
                  <label className={classes.mediaControlLabel}>
                    Режим отображения
                    <select
                      className={classes.displayModeSelect}
                      value={tabloSettings.backgroundFitMode ?? "cover"}
                      onChange={(event) =>
                        updateTabloSettings({
                          backgroundFitMode: event.target
                            .value as TerminalTabloSettings["backgroundFitMode"],
                        })
                      }
                    >
                      <option value="cover">Заполнить область</option>
                      <option value="contain">Вписать в область</option>
                      <option value="stretch">
                        Растянуть (по ширине и высоте)
                      </option>
                      <option value="width">Растянуть по ширине</option>
                      <option value="height">Растянуть по высоте</option>
                      <option value="none">Оригинальный размер</option>
                    </select>
                  </label>
                </div>
                <div className={classes.backgroundList}>
                  <label className={classes.backgroundNoneItem}>
                    <input
                      type="radio"
                      name="tabloActiveBackground"
                      checked={!tabloSettings.backgroundId}
                      onChange={() =>
                        updateTabloSettings({ backgroundId: null })
                      }
                    />
                    <span>Без медиа фона</span>
                  </label>
                  {backgrounds.map((background: TerminalBackgroundMedia) => (
                    <div key={background.id} className={classes.backgroundItem}>
                      <label className={classes.backgroundHeader}>
                        <input
                          type="radio"
                          name="tabloActiveBackground"
                          checked={tabloSettings.backgroundId === background.id}
                          onChange={() =>
                            updateTabloSettings({ backgroundId: background.id })
                          }
                        />
                        <span>{background.fileName}</span>
                      </label>
                      <div className={classes.backgroundPreview}>
                        {background.mediaType === "IMAGE" ? (
                          <img src={background.url} alt={background.fileName} />
                        ) : (
                          <video src={background.url} muted />
                        )}
                      </div>
                      <div className={classes.backgroundActions}>
                        <CustomButton
                          content="Удалить"
                          styleType="default"
                          onClick={() => handleDeleteBackground(background.id)}
                        />
                      </div>
                    </div>
                  ))}
                  {isDeleteTerminalBackgroundLoading && (
                    <span>Удаление...</span>
                  )}
                </div>
                <div className={classes.tabloColorControls}>
                  <div className={classes.gradientField}>
                    <span>Цвет фона</span>
                    <input
                      type="color"
                      value={tabloBackgroundColor.hex}
                      onChange={(event) =>
                        updateTabloSettings({
                          backgroundColor: buildColorWithAlpha(
                            event.target.value,
                            tabloBackgroundColor.alpha,
                          ),
                        })
                      }
                    />
                    <span>Непрозрачность</span>
                    <div className={classes.rangeRow}>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={Math.round(tabloBackgroundColor.alpha * 100)}
                        onChange={(event) =>
                          updateTabloSettings({
                            backgroundColor: buildColorWithAlpha(
                              tabloBackgroundColor.hex,
                              clampNumber(Number(event.target.value), 0, 100) /
                                100,
                            ),
                          })
                        }
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={Math.round(tabloBackgroundColor.alpha * 100)}
                        onChange={(event) =>
                          updateTabloSettings({
                            backgroundColor: buildColorWithAlpha(
                              tabloBackgroundColor.hex,
                              clampNumber(Number(event.target.value), 0, 100) /
                                100,
                            ),
                          })
                        }
                      />
                      <span>%</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("elements")}
            >
              <h3>Элементы табло</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.elements ? classes.collapseIconExpanded : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.elements ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.elements && (
              <div className={classes.elements}>
                {(Object.keys(tabloSettings.elements) as TabloElementKey[]).map(
                  (key) => {
                    const element = tabloSettings.elements[key];
                    const elementBgColor = parseColorWithAlpha(
                      element.backgroundColor,
                      "#000000",
                    );
                    const elementTextColor = parseColorWithAlpha(
                      element.textColor ?? undefined,
                      "#ffffff",
                    );
                    const isExpanded = expandedElements[key];
                    return (
                      <div key={key} className={classes.elementCard}>
                        <div className={classes.elementHeader}>
                          <button
                            type="button"
                            className={classes.elementHeaderMain}
                            onClick={() => toggleElementExpanded(key)}
                          >
                            <h4>{elementLabels[key]}</h4>
                            <span
                              className={`${classes.collapseIcon} ${
                                isExpanded ? classes.collapseIconExpanded : ""
                              }`}
                              aria-hidden="true"
                            >
                              {isExpanded ? "▲" : "▼"}
                            </span>
                          </button>
                          <label className={classes.toggleRow}>
                            <input
                              type="checkbox"
                              checked={element.isEnabled}
                              onChange={(event) =>
                                updateTabloElement(key, {
                                  isEnabled: event.target.checked,
                                })
                              }
                            />
                            Показывать
                          </label>
                        </div>
                        {isExpanded && (
                          <div className={classes.elementBody}>
                            {key === "video" && (
                              <>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "14px",
                                  }}
                                >
                                  <label className={classes.sliderUploadRow}>
                                    Видео (mp4, webm, ogg, mkv)
                                    <input
                                      type="file"
                                      accept="video/mp4,video/webm,video/ogg,video/x-matroska"
                                      disabled={isUploadTabloVideoLoading}
                                      onChange={(event) => {
                                        const file = event.target.files?.[0];
                                        if (!file) return;
                                        uploadTabloVideo(file, {
                                          onSuccess: (res) => {
                                            const url = res.data?.url;
                                            if (url) {
                                              updateTabloElement("video", {
                                                videoUrls: [
                                                  ...(tabloSettings.elements
                                                    .video.videoUrls ?? []),
                                                  url,
                                                ],
                                              });
                                            }
                                          },
                                        });
                                        event.target.value = "";
                                      }}
                                    />
                                    {isUploadTabloVideoLoading && (
                                      <span>Загрузка...</span>
                                    )}
                                  </label>
                                  <div className={classes.sliderImageList}>
                                    {(element.videoUrls ?? []).map(
                                      (url, index) => (
                                        <div
                                          key={`${url}-${index}`}
                                          className={classes.sliderImageItem}
                                          draggable
                                          onDragStart={() =>
                                            setVideoDragIndex(index)
                                          }
                                          onDragOver={(e) => {
                                            e.preventDefault();
                                            e.currentTarget.classList.add(
                                              classes.sliderImageItemDragOver,
                                            );
                                          }}
                                          onDragLeave={(e) => {
                                            e.currentTarget.classList.remove(
                                              classes.sliderImageItemDragOver,
                                            );
                                          }}
                                          onDrop={(e) => {
                                            e.preventDefault();
                                            e.currentTarget.classList.remove(
                                              classes.sliderImageItemDragOver,
                                            );
                                            if (
                                              videoDragIndex === null ||
                                              videoDragIndex === index
                                            ) {
                                              setVideoDragIndex(null);
                                              return;
                                            }
                                            const urls = [
                                              ...(element.videoUrls ?? []),
                                            ];
                                            const [removed] = urls.splice(
                                              videoDragIndex,
                                              1,
                                            );
                                            urls.splice(index, 0, removed);
                                            updateTabloElement("video", {
                                              videoUrls: urls,
                                            });
                                            setVideoDragIndex(null);
                                          }}
                                          onDragEnd={() =>
                                            setVideoDragIndex(null)
                                          }
                                        >
                                          <video
                                            src={normalizeMediaUrl(url)}
                                            className={classes.sliderImageThumb}
                                            muted
                                            preload="metadata"
                                            draggable={false}
                                          />
                                          <CustomButton
                                            content="Удалить"
                                            styleType="default"
                                            onClick={() => {
                                              const urls = (
                                                element.videoUrls ?? []
                                              ).filter((_, i) => i !== index);
                                              updateTabloElement("video", {
                                                videoUrls: urls,
                                              });
                                            }}
                                          />
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>
                                <div className={classes.videoPlaylistBlock}>
                                  {/* <span>Плейлист видео</span>
                                {(element.videoPlaylistIds ?? []).map((videoId, idx) => {
                                  const video = allVideos.find((v) => v.id === videoId);
                                  return (
                                    <div key={`${videoId}-${idx}`} className={classes.tickerTextRow}>
                                      <span className={classes.videoPlaylistName}>
                                        {video ? video.fileName : `ID ${videoId}`}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const arr = (element.videoPlaylistIds ?? []).filter(
                                            (_, i) => i !== idx
                                          );
                                          updateTabloElement('video', { videoPlaylistIds: arr });
                                        }}
                                        aria-label="Удалить из плейлиста">
                                        ×
                                      </button>
                                    </div>
                                  );
                                })}
                                <select
                                  value={videoPlaylistSelectValue}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    setVideoPlaylistSelectValue('');
                                    const id = Number(value);
                                    if (!Number.isFinite(id)) return;
                                    const arr = [...(element.videoPlaylistIds ?? []), id];
                                    updateTabloElement('video', { videoPlaylistIds: arr });
                                  }}>
                                  <option value="">
                                    {allVideos.filter(
                                      (v) => !(element.videoPlaylistIds ?? []).includes(v.id)
                                    ).length === 0
                                      ? 'Нет доступных видео'
                                      : 'Добавить видео...'}
                                  </option>
                                  {allVideos
                                    .filter(
                                      (v) =>
                                        !(element.videoPlaylistIds ?? []).includes(v.id)
                                    )
                                    .map((v) => (
                                      <option key={v.id} value={String(v.id)}>
                                        {v.fileName}
                                      </option>
                                    ))}
                                </select> */}
                                </div>
                                <label className={classes.toggleRow}>
                                  <input
                                    type="checkbox"
                                    checked={
                                      !!element.videoFullScreenWhenNoTickets
                                    }
                                    onChange={(event) =>
                                      updateTabloElement("video", {
                                        videoFullScreenWhenNoTickets:
                                          event.target.checked,
                                      })
                                    }
                                  />
                                  Видеоблок во весь экран, если нет талонов в
                                  очереди
                                </label>
                              </>
                            )}
                            {key === "queue" && (
                              <>
                                <label className={classes.toggleRow}>
                                  <input
                                    type="checkbox"
                                    checked={!!element.queueTwoColumns}
                                    onChange={(event) =>
                                      updateTabloElement("queue", {
                                        queueTwoColumns: event.target.checked,
                                      })
                                    }
                                  />
                                  Две колонки
                                </label>
                                <div style={{ marginTop: "11px" }}></div>
                                {(() => {
                                  const ticketBlinkColor = parseColorWithAlpha(
                                    element.ticketBlinkBGColor,
                                    "rgba(76, 175, 80, 0.6)",
                                  );
                                  const ticketColor = parseColorWithAlpha(
                                    element.ticketBGColor,
                                    "#0066ff",
                                  );
                                  return (
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        gap: "14px",
                                      }}
                                    >
                                      <div className={classes.gradientField}>
                                        <span>Цвет фона вызванного номера</span>
                                        <input
                                          type="color"
                                          value={ticketBlinkColor.hex}
                                          onChange={(event) =>
                                            updateTabloElement("queue", {
                                              ticketBlinkBGColor:
                                                buildColorWithAlpha(
                                                  event.target.value,
                                                  ticketBlinkColor.alpha,
                                                ),
                                            })
                                          }
                                        />
                                        <span>Непрозрачность</span>
                                        <div className={classes.rangeRow}>
                                          <input
                                            type="range"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={Math.round(
                                              ticketBlinkColor.alpha * 100,
                                            )}
                                            onChange={(event) =>
                                              updateTabloElement("queue", {
                                                ticketBlinkBGColor:
                                                  buildColorWithAlpha(
                                                    ticketBlinkColor.hex,
                                                    clampNumber(
                                                      Number(
                                                        event.target.value,
                                                      ),
                                                      0,
                                                      100,
                                                    ) / 100,
                                                  ),
                                              })
                                            }
                                          />
                                          <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={Math.round(
                                              ticketBlinkColor.alpha * 100,
                                            )}
                                            onChange={(event) =>
                                              updateTabloElement("queue", {
                                                ticketBlinkBGColor:
                                                  buildColorWithAlpha(
                                                    ticketBlinkColor.hex,
                                                    clampNumber(
                                                      Number(
                                                        event.target.value,
                                                      ),
                                                      0,
                                                      100,
                                                    ) / 100,
                                                  ),
                                              })
                                            }
                                          />
                                          <span>%</span>
                                        </div>
                                      </div>
                                      <div className={classes.gradientField}>
                                        <span>
                                          Цвет фона номера, взятого в работу
                                        </span>
                                        <input
                                          type="color"
                                          value={ticketColor.hex}
                                          onChange={(event) =>
                                            updateTabloElement("queue", {
                                              ticketBGColor:
                                                buildColorWithAlpha(
                                                  event.target.value,
                                                  ticketColor.alpha,
                                                ),
                                            })
                                          }
                                        />
                                        <span>Непрозрачность</span>
                                        <div className={classes.rangeRow}>
                                          <input
                                            type="range"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={Math.round(
                                              ticketColor.alpha * 100,
                                            )}
                                            onChange={(event) =>
                                              updateTabloElement("queue", {
                                                ticketBGColor:
                                                  buildColorWithAlpha(
                                                    ticketColor.hex,
                                                    clampNumber(
                                                      Number(
                                                        event.target.value,
                                                      ),
                                                      0,
                                                      100,
                                                    ) / 100,
                                                  ),
                                              })
                                            }
                                          />
                                          <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={Math.round(
                                              ticketColor.alpha * 100,
                                            )}
                                            onChange={(event) =>
                                              updateTabloElement("queue", {
                                                ticketBGColor:
                                                  buildColorWithAlpha(
                                                    ticketColor.hex,
                                                    clampNumber(
                                                      Number(
                                                        event.target.value,
                                                      ),
                                                      0,
                                                      100,
                                                    ) / 100,
                                                  ),
                                              })
                                            }
                                          />
                                          <span>%</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </>
                            )}
                            {key === "takenTickets" && (
                              <>
                                <label className={classes.toggleRow}>
                                  <input
                                    type="checkbox"
                                    checked={!!element.takenTicketsSortOldToNew}
                                    onChange={(event) =>
                                      updateTabloElement("takenTickets", {
                                        takenTicketsSortOldToNew:
                                          event.target.checked,
                                      })
                                    }
                                  />
                                  От старых к новым
                                </label>
                                <div style={{ marginTop: "11px" }}></div>
                              </>
                            )}
                            {key === "slider" && (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "14px",
                                }}
                              >
                                <label className={classes.sliderUploadRow}>
                                  Изображения (jpeg, png, webp, gif, svg)
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                                    disabled={isUploadTabloSliderImageLoading}
                                    onChange={(event) => {
                                      const file = event.target.files?.[0];
                                      if (!file) return;
                                      uploadTabloSliderImage(file, {
                                        onSuccess: (res) => {
                                          const url = res.data?.url;
                                          if (url) {
                                            updateTabloElement("slider", {
                                              imageUrls: [
                                                ...(tabloSettings.elements
                                                  .slider.imageUrls ?? []),
                                                url,
                                              ],
                                            });
                                          }
                                        },
                                      });
                                      event.target.value = "";
                                    }}
                                  />
                                  {isUploadTabloSliderImageLoading && (
                                    <span>Загрузка...</span>
                                  )}
                                </label>
                                <label>
                                  Скорость перелистывания слайдов (сек){" "}
                                  <span style={{ color: "red" }}> </span>
                                  <input
                                    type="number"
                                    min={1}
                                    max={120}
                                    step={1}
                                    value={element.slideIntervalSeconds ?? 5}
                                    onChange={(e) => {
                                      const v = parseNumberInput(
                                        e.target.value,
                                      );
                                      const clamped =
                                        v != null && Number.isFinite(v)
                                          ? Math.min(120, Math.max(1, v))
                                          : 5;
                                      updateTabloElement("slider", {
                                        slideIntervalSeconds: clamped,
                                      });
                                    }}
                                  />
                                </label>
                                <label>
                                  Режим отображения{" "}
                                  <span style={{ color: "red" }}> </span>
                                  <select
                                    className={classes.displayModeSelect}
                                    value={element.fitMode ?? "contain"}
                                    onChange={(e) =>
                                      updateTabloElement("slider", {
                                        fitMode: e.target
                                          .value as TerminalTabloElementSettings["fitMode"],
                                      })
                                    }
                                  >
                                    <option value="scale">Свой размер</option>
                                    <option value="contain">
                                      Вписать в область
                                    </option>
                                    <option value="cover">
                                      Заполнить область
                                    </option>
                                    <option value="stretch">
                                      Растянуть (по ширине и высоте)
                                    </option>
                                    <option value="width">
                                      Растянуть по ширине
                                    </option>
                                    <option value="height">
                                      Растянуть по высоте
                                    </option>
                                    <option value="none">
                                      Оригинальный размер
                                    </option>
                                  </select>
                                </label>
                                <div className={classes.sliderImageList}>
                                  {(element.imageUrls ?? []).map(
                                    (url, index) => (
                                      <div
                                        key={`${url}-${index}`}
                                        className={classes.sliderImageItem}
                                        draggable
                                        onDragStart={() =>
                                          setSliderDragIndex(index)
                                        }
                                        onDragOver={(e) => {
                                          e.preventDefault();
                                          e.currentTarget.classList.add(
                                            classes.sliderImageItemDragOver,
                                          );
                                        }}
                                        onDragLeave={(e) => {
                                          e.currentTarget.classList.remove(
                                            classes.sliderImageItemDragOver,
                                          );
                                        }}
                                        onDrop={(e) => {
                                          e.preventDefault();
                                          e.currentTarget.classList.remove(
                                            classes.sliderImageItemDragOver,
                                          );
                                          if (
                                            sliderDragIndex === null ||
                                            sliderDragIndex === index
                                          ) {
                                            setSliderDragIndex(null);
                                            return;
                                          }
                                          const urls = [
                                            ...(element.imageUrls ?? []),
                                          ];
                                          const [removed] = urls.splice(
                                            sliderDragIndex,
                                            1,
                                          );
                                          urls.splice(index, 0, removed);
                                          updateTabloElement("slider", {
                                            imageUrls: urls,
                                          });
                                          setSliderDragIndex(null);
                                        }}
                                        onDragEnd={() =>
                                          setSliderDragIndex(null)
                                        }
                                      >
                                        <img
                                          src={normalizeMediaUrl(url)}
                                          alt=""
                                          className={classes.sliderImageThumb}
                                          draggable={false}
                                        />
                                        <CustomButton
                                          content="Удалить"
                                          styleType="default"
                                          onClick={() => {
                                            const urls = (
                                              element.imageUrls ?? []
                                            ).filter((_, i) => i !== index);
                                            updateTabloElement("slider", {
                                              imageUrls: urls,
                                            });
                                          }}
                                        />
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                            {key === "weatherWidget" && (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "14px",
                                }}
                              >
                                <label>
                                  Регион / город для погоды
                                  <input
                                    type="text"
                                    className={classes.displayModeSelect}
                                    placeholder="Например: Минск, Гродно"
                                    value={element.weatherRegion ?? ""}
                                    onChange={(e) =>
                                      updateTabloElement("weatherWidget", {
                                        weatherRegion:
                                          e.target.value || undefined,
                                      })
                                    }
                                  />
                                </label>
                                <label>
                                  Шрифт
                                  <select
                                    className={classes.displayModeSelect}
                                    value={element.fontFamily ?? "SFPro"}
                                    onChange={(e) =>
                                      updateTabloElement("weatherWidget", {
                                        fontFamily: e.target.value,
                                      })
                                    }
                                  >
                                    {fontOptions.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label>
                                  Начертание
                                  <select
                                    className={classes.displayModeSelect}
                                    value={element.fontWeight ?? "600"}
                                    onChange={(e) =>
                                      updateTabloElement("weatherWidget", {
                                        fontWeight: e.target.value,
                                      })
                                    }
                                  >
                                    {fontWeights.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label>
                                  Размер шрифта — регион (px)
                                  <input
                                    type="number"
                                    min={8}
                                    max={72}
                                    value={
                                      editingWeatherRegionFontSize ??
                                      String(
                                        element.weatherRegionFontSize ?? 14,
                                      )
                                    }
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      setEditingWeatherRegionFontSize(raw);
                                      const v = parseNumberInput(raw);
                                      if (v != null && Number.isFinite(v)) {
                                        const clamped = Math.min(
                                          72,
                                          Math.max(8, v),
                                        );
                                        updateTabloElement("weatherWidget", {
                                          weatherRegionFontSize: clamped,
                                        });
                                      }
                                    }}
                                    onBlur={() => {
                                      const v = parseNumberInput(
                                        editingWeatherRegionFontSize ?? "",
                                      );
                                      const clamped =
                                        v != null && Number.isFinite(v)
                                          ? Math.min(72, Math.max(8, v))
                                          : 14;
                                      updateTabloElement("weatherWidget", {
                                        weatherRegionFontSize: clamped,
                                      });
                                      setEditingWeatherRegionFontSize(null);
                                    }}
                                  />
                                </label>
                                <label>
                                  Размер шрифта — температура (px)
                                  <input
                                    type="number"
                                    min={14}
                                    max={96}
                                    value={
                                      editingWeatherTempFontSize ??
                                      String(element.weatherTempFontSize ?? 28)
                                    }
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      setEditingWeatherTempFontSize(raw);
                                      const v = parseNumberInput(raw);
                                      if (v != null && Number.isFinite(v)) {
                                        const clamped = Math.min(
                                          96,
                                          Math.max(14, v),
                                        );
                                        updateTabloElement("weatherWidget", {
                                          weatherTempFontSize: clamped,
                                        });
                                      }
                                    }}
                                    onBlur={() => {
                                      const v = parseNumberInput(
                                        editingWeatherTempFontSize ?? "",
                                      );
                                      const clamped =
                                        v != null && Number.isFinite(v)
                                          ? Math.min(96, Math.max(14, v))
                                          : 28;
                                      updateTabloElement("weatherWidget", {
                                        weatherTempFontSize: clamped,
                                      });
                                      setEditingWeatherTempFontSize(null);
                                    }}
                                  />
                                </label>
                                <label>
                                  Размер шрифта — описание (px)
                                  <input
                                    type="number"
                                    min={8}
                                    max={48}
                                    value={
                                      editingWeatherDescriptionFontSize ??
                                      String(
                                        element.weatherDescriptionFontSize ??
                                          12,
                                      )
                                    }
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      setEditingWeatherDescriptionFontSize(raw);
                                      const v = parseNumberInput(raw);
                                      if (v != null && Number.isFinite(v)) {
                                        const clamped = Math.min(
                                          48,
                                          Math.max(8, v),
                                        );
                                        updateTabloElement("weatherWidget", {
                                          weatherDescriptionFontSize: clamped,
                                        });
                                      }
                                    }}
                                    onBlur={() => {
                                      const v = parseNumberInput(
                                        editingWeatherDescriptionFontSize ?? "",
                                      );
                                      const clamped =
                                        v != null && Number.isFinite(v)
                                          ? Math.min(48, Math.max(8, v))
                                          : 12;
                                      updateTabloElement("weatherWidget", {
                                        weatherDescriptionFontSize: clamped,
                                      });
                                      setEditingWeatherDescriptionFontSize(
                                        null,
                                      );
                                    }}
                                  />
                                </label>
                                {(() => {
                                  const weatherTextColor = parseColorWithAlpha(
                                    element.textColor ?? undefined,
                                    "#ffffff",
                                  );
                                  return (
                                    <div className={classes.gradientField}>
                                      <span>Цвет текста</span>
                                      <input
                                        type="color"
                                        value={weatherTextColor.hex}
                                        onChange={(e) =>
                                          updateTabloElement("weatherWidget", {
                                            textColor: buildColorWithAlpha(
                                              e.target.value,
                                              weatherTextColor.alpha,
                                            ),
                                          })
                                        }
                                      />
                                      <span>Непрозрачность</span>
                                      <div className={classes.rangeRow}>
                                        <input
                                          type="range"
                                          min={0}
                                          max={100}
                                          step={1}
                                          value={Math.round(
                                            weatherTextColor.alpha * 100,
                                          )}
                                          onChange={(e) =>
                                            updateTabloElement(
                                              "weatherWidget",
                                              {
                                                textColor: buildColorWithAlpha(
                                                  weatherTextColor.hex,
                                                  clampNumber(
                                                    Number(e.target.value),
                                                    0,
                                                    100,
                                                  ) / 100,
                                                ),
                                              },
                                            )
                                          }
                                        />
                                        <input
                                          type="number"
                                          min={0}
                                          max={100}
                                          value={Math.round(
                                            weatherTextColor.alpha * 100,
                                          )}
                                          onChange={(e) =>
                                            updateTabloElement(
                                              "weatherWidget",
                                              {
                                                textColor: buildColorWithAlpha(
                                                  weatherTextColor.hex,
                                                  clampNumber(
                                                    Number(e.target.value),
                                                    0,
                                                    100,
                                                  ) / 100,
                                                ),
                                              },
                                            )
                                          }
                                        />
                                        <span>%</span>
                                      </div>
                                    </div>
                                  );
                                })()}
                                <>
                                  <label>
                                    Размер иконки погоды (px)
                                    <input
                                      type="number"
                                      min={16}
                                      max={200}
                                      value={
                                        editingWeatherIconSize ??
                                        String(element.weatherIconSize ?? 40)
                                      }
                                      onChange={(e) => {
                                        const raw = e.target.value;
                                        setEditingWeatherIconSize(raw);
                                        const v = parseNumberInput(raw);
                                        if (v != null && Number.isFinite(v)) {
                                          const clamped = Math.min(
                                            200,
                                            Math.max(16, v),
                                          );
                                          updateTabloElement("weatherWidget", {
                                            weatherIconSize: clamped,
                                          });
                                        }
                                      }}
                                      onBlur={() => {
                                        const v = parseNumberInput(
                                          editingWeatherIconSize ?? "",
                                        );
                                        const clamped =
                                          v != null && Number.isFinite(v)
                                            ? Math.min(200, Math.max(16, v))
                                            : 40;
                                        updateTabloElement("weatherWidget", {
                                          weatherIconSize: clamped,
                                        });
                                        setEditingWeatherIconSize(null);
                                      }}
                                    />
                                  </label>
                                  <div style={{ marginTop: "6px" }}></div>
                                </>
                              </div>
                            )}
                            {key === "rssBeltaWidget" && (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "14px",
                                }}
                              >
                                <label>
                                  Количество новостей
                                  <input
                                    type="number"
                                    min={1}
                                    max={20}
                                    step={1}
                                    value={
                                      editingRssNewsCount ??
                                      String(element.rssNewsCount ?? 5)
                                    }
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      setEditingRssNewsCount(raw);
                                      const v = parseNumberInput(raw);
                                      if (v != null && Number.isFinite(v)) {
                                        updateTabloElement("rssBeltaWidget", {
                                          rssNewsCount: Math.min(
                                            20,
                                            Math.max(1, v),
                                          ),
                                        });
                                      }
                                    }}
                                    onBlur={() => {
                                      const v = parseNumberInput(
                                        editingRssNewsCount ?? "",
                                      );
                                      const clamped =
                                        v != null && Number.isFinite(v)
                                          ? Math.min(20, Math.max(1, v))
                                          : 5;
                                      updateTabloElement("rssBeltaWidget", {
                                        rssNewsCount: clamped,
                                      });
                                      setEditingRssNewsCount(null);
                                    }}
                                  />
                                </label>
                                <label>
                                  Интервал смены новости (сек)
                                  <input
                                    type="number"
                                    min={1}
                                    max={120}
                                    step={1}
                                    value={
                                      editingRssSwitchInterval ??
                                      String(
                                        element.rssSwitchIntervalSeconds ?? 10,
                                      )
                                    }
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      setEditingRssSwitchInterval(raw);
                                      const v = parseNumberInput(raw);
                                      if (v != null && Number.isFinite(v)) {
                                        updateTabloElement("rssBeltaWidget", {
                                          rssSwitchIntervalSeconds: Math.min(
                                            120,
                                            Math.max(1, v),
                                          ),
                                        });
                                      }
                                    }}
                                    onBlur={() => {
                                      const v = parseNumberInput(
                                        editingRssSwitchInterval ?? "",
                                      );
                                      const clamped =
                                        v != null && Number.isFinite(v)
                                          ? Math.min(120, Math.max(1, v))
                                          : 10;
                                      updateTabloElement("rssBeltaWidget", {
                                        rssSwitchIntervalSeconds: clamped,
                                      });
                                      setEditingRssSwitchInterval(null);
                                    }}
                                  />
                                </label>
                                <label>
                                  Период обновления данных (мин)
                                  <input
                                    type="number"
                                    min={1}
                                    max={120}
                                    step={1}
                                    value={
                                      editingRssRefreshPeriod ??
                                      String(
                                        element.rssRefreshPeriodMinutes ?? 10,
                                      )
                                    }
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      setEditingRssRefreshPeriod(raw);
                                      const v = parseNumberInput(raw);
                                      if (v != null && Number.isFinite(v)) {
                                        updateTabloElement("rssBeltaWidget", {
                                          rssRefreshPeriodMinutes: Math.min(
                                            120,
                                            Math.max(1, v),
                                          ),
                                        });
                                      }
                                    }}
                                    onBlur={() => {
                                      const v = parseNumberInput(
                                        editingRssRefreshPeriod ?? "",
                                      );
                                      const clamped =
                                        v != null && Number.isFinite(v)
                                          ? Math.min(120, Math.max(1, v))
                                          : 10;
                                      updateTabloElement("rssBeltaWidget", {
                                        rssRefreshPeriodMinutes: clamped,
                                      });
                                      setEditingRssRefreshPeriod(null);
                                    }}
                                  />
                                </label>
                                <label>
                                  Шрифт
                                  <select
                                    className={classes.displayModeSelect}
                                    value={element.fontFamily ?? "SFPro"}
                                    onChange={(e) =>
                                      updateTabloElement("rssBeltaWidget", {
                                        fontFamily: e.target.value,
                                      })
                                    }
                                  >
                                    {fontOptions.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label>
                                  Начертание
                                  <select
                                    className={classes.displayModeSelect}
                                    value={element.fontWeight ?? "600"}
                                    onChange={(e) =>
                                      updateTabloElement("rssBeltaWidget", {
                                        fontWeight: e.target.value,
                                      })
                                    }
                                  >
                                    {fontWeights.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label>
                                  Размер шрифта заголовка (px)
                                  <input
                                    type="number"
                                    min={10}
                                    max={48}
                                    value={
                                      editingRssFontSize ??
                                      String(element.fontSize ?? 16)
                                    }
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      setEditingRssFontSize(raw);
                                      const v = parseNumberInput(raw);
                                      if (v != null && Number.isFinite(v)) {
                                        updateTabloElement("rssBeltaWidget", {
                                          fontSize: Math.min(
                                            48,
                                            Math.max(10, v),
                                          ),
                                        });
                                      }
                                    }}
                                    onBlur={() => {
                                      const v = parseNumberInput(
                                        editingRssFontSize ?? "",
                                      );
                                      const clamped =
                                        v != null && Number.isFinite(v)
                                          ? Math.min(48, Math.max(10, v))
                                          : 16;
                                      updateTabloElement("rssBeltaWidget", {
                                        fontSize: clamped,
                                      });
                                      setEditingRssFontSize(null);
                                    }}
                                  />
                                </label>
                                {(() => {
                                  const rssTextColor = parseColorWithAlpha(
                                    element.textColor ?? undefined,
                                    "#ffffff",
                                  );
                                  return (
                                    <div className={classes.gradientField}>
                                      <span>Цвет текста</span>
                                      <input
                                        type="color"
                                        value={rssTextColor.hex}
                                        onChange={(e) =>
                                          updateTabloElement(
                                            "rssBeltaWidget",
                                            {
                                              textColor: buildColorWithAlpha(
                                                e.target.value,
                                                rssTextColor.alpha,
                                              ),
                                            },
                                          )
                                        }
                                      />
                                      <span>Непрозрачность</span>
                                      <div className={classes.rangeRow}>
                                        <input
                                          type="range"
                                          min={0}
                                          max={100}
                                          step={1}
                                          value={Math.round(
                                            rssTextColor.alpha * 100,
                                          )}
                                          onChange={(e) =>
                                            updateTabloElement(
                                              "rssBeltaWidget",
                                              {
                                                textColor: buildColorWithAlpha(
                                                  rssTextColor.hex,
                                                  clampNumber(
                                                    Number(e.target.value),
                                                    0,
                                                    100,
                                                  ) / 100,
                                                ),
                                              },
                                            )
                                          }
                                        />
                                        <span>
                                          {Math.round(rssTextColor.alpha * 100)}
                                          %
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                            <div className={classes.grid}>
                              <label>
                                X (%)
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={element.x}
                                  onChange={(event) =>
                                    updateTabloElement(key, {
                                      x: Number(event.target.value),
                                    })
                                  }
                                />
                              </label>
                              <label>
                                Y (%)
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={element.y}
                                  onChange={(event) =>
                                    updateTabloElement(key, {
                                      y: Number(event.target.value),
                                    })
                                  }
                                />
                              </label>
                              <label>
                                Ширина (%)
                                <input
                                  type="number"
                                  min={1}
                                  max={100}
                                  value={element.width}
                                  onChange={(event) =>
                                    updateTabloElement(key, {
                                      width: Number(event.target.value),
                                    })
                                  }
                                />
                              </label>
                              <label>
                                Высота (%)
                                <input
                                  type="number"
                                  min={1}
                                  max={100}
                                  value={element.height}
                                  onChange={(event) =>
                                    updateTabloElement(key, {
                                      height: Number(event.target.value),
                                    })
                                  }
                                />
                              </label>
                              {elementHasNumberSize[key] && (
                                <>
                                  <label>
                                    Ширина номера (px)
                                    <input
                                      type="number"
                                      min={40}
                                      max={1000}
                                      value={element.numberWidth ?? 195}
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          numberWidth: Number(
                                            event.target.value,
                                          ),
                                        })
                                      }
                                    />
                                  </label>
                                  <label>
                                    Высота номера (px)
                                    <input
                                      type="number"
                                      min={24}
                                      max={600}
                                      value={element.numberHeight ?? 90}
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          numberHeight: Number(
                                            event.target.value,
                                          ),
                                        })
                                      }
                                    />
                                  </label>
                                </>
                              )}
                              <div className={classes.gradientField}>
                                <span>Цвет фона</span>
                                <input
                                  type="color"
                                  value={elementBgColor.hex}
                                  onChange={(event) =>
                                    updateTabloElement(key, {
                                      backgroundColor: buildColorWithAlpha(
                                        event.target.value,
                                        elementBgColor.alpha,
                                      ),
                                    })
                                  }
                                />
                                <span>Непрозрачность</span>
                                <div className={classes.rangeRow}>
                                  <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={Math.round(
                                      elementBgColor.alpha * 100,
                                    )}
                                    onChange={(event) =>
                                      updateTabloElement(key, {
                                        backgroundColor: buildColorWithAlpha(
                                          elementBgColor.hex,
                                          clampNumber(
                                            Number(event.target.value),
                                            0,
                                            100,
                                          ) / 100,
                                        ),
                                      })
                                    }
                                  />
                                  <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={Math.round(
                                      elementBgColor.alpha * 100,
                                    )}
                                    onChange={(event) =>
                                      updateTabloElement(key, {
                                        backgroundColor: buildColorWithAlpha(
                                          elementBgColor.hex,
                                          clampNumber(
                                            Number(event.target.value),
                                            0,
                                            100,
                                          ) / 100,
                                        ),
                                      })
                                    }
                                  />
                                  <span>%</span>
                                </div>
                              </div>
                              <label>
                                Размытие фона (px)
                                <input
                                  type="number"
                                  min={0}
                                  max={50}
                                  value={element.backdropBlur ?? 0}
                                  onChange={(event) =>
                                    updateTabloElement(key, {
                                      backdropBlur: Number(event.target.value),
                                    })
                                  }
                                />
                              </label>
                              {key === "title" && (
                                <>
                                  <label>
                                    Текст заголовка
                                    <input
                                      type="text"
                                      value={element.titleText ?? ""}
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          titleText: event.target.value,
                                        })
                                      }
                                      placeholder="Заголовок табло"
                                    />
                                  </label>
                                  <label>
                                    Текст подзаголовка
                                    <input
                                      type="text"
                                      value={element.subTitleText ?? ""}
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          subTitleText: event.target.value,
                                        })
                                      }
                                      placeholder="Подзаголовок табло"
                                    />
                                  </label>
                                  <label>
                                    Шрифт заголовка
                                    <select
                                      value={
                                        element.titleFontFamily ??
                                        element.fontFamily ??
                                        fontOptions[0].value
                                      }
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          titleFontFamily: event.target.value,
                                        })
                                      }
                                    >
                                      {fontOptions.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label>
                                    Начертание заголовка
                                    <select
                                      value={
                                        element.titleFontWeight ??
                                        element.fontWeight ??
                                        fontWeights[0].value
                                      }
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          titleFontWeight: event.target.value,
                                        })
                                      }
                                    >
                                      {fontWeights.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label>
                                    Размер шрифта заголовка
                                    <input
                                      type="number"
                                      min={10}
                                      max={120}
                                      value={
                                        element.titleFontSize ??
                                        element.fontSize ??
                                        36
                                      }
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          titleFontSize:
                                            Number(event.target.value) ||
                                            undefined,
                                        })
                                      }
                                    />
                                  </label>
                                  <label>
                                    Высота строки заголовка (px)
                                    <input
                                      type="number"
                                      min={10}
                                      max={200}
                                      value={element.titleLineHeight ?? ""}
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          titleLineHeight: parseNumberInput(
                                            event.target.value,
                                          ),
                                        })
                                      }
                                    />
                                  </label>
                                  {(() => {
                                    const titleTextColor = parseColorWithAlpha(
                                      element.titleTextColor ??
                                        element.textColor ??
                                        undefined,
                                      "#ffffff",
                                    );
                                    return (
                                      <div className={classes.gradientField}>
                                        <span>Цвет текста заголовка</span>
                                        <input
                                          type="color"
                                          value={titleTextColor.hex}
                                          onChange={(event) =>
                                            updateTabloElement(key, {
                                              titleTextColor:
                                                buildColorWithAlpha(
                                                  event.target.value,
                                                  titleTextColor.alpha,
                                                ),
                                            })
                                          }
                                        />
                                        <span>Непрозрачность</span>
                                        <div className={classes.rangeRow}>
                                          <input
                                            type="range"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={Math.round(
                                              titleTextColor.alpha * 100,
                                            )}
                                            onChange={(event) =>
                                              updateTabloElement(key, {
                                                titleTextColor:
                                                  buildColorWithAlpha(
                                                    titleTextColor.hex,
                                                    clampNumber(
                                                      Number(
                                                        event.target.value,
                                                      ),
                                                      0,
                                                      100,
                                                    ) / 100,
                                                  ),
                                              })
                                            }
                                          />
                                          <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={Math.round(
                                              titleTextColor.alpha * 100,
                                            )}
                                            onChange={(event) =>
                                              updateTabloElement(key, {
                                                titleTextColor:
                                                  buildColorWithAlpha(
                                                    titleTextColor.hex,
                                                    clampNumber(
                                                      Number(
                                                        event.target.value,
                                                      ),
                                                      0,
                                                      100,
                                                    ) / 100,
                                                  ),
                                              })
                                            }
                                          />
                                          <span>%</span>
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  <div className={classes.subSectionTitle}>
                                    {/* <span>Подзаголовок</span> */}
                                  </div>
                                  <label>
                                    Шрифт подзаголовка
                                    <select
                                      value={
                                        element.subTitleFontFamily ??
                                        element.fontFamily ??
                                        fontOptions[0].value
                                      }
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          subTitleFontFamily:
                                            event.target.value,
                                        })
                                      }
                                    >
                                      {fontOptions.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label>
                                    Начертание подзаголовка
                                    <select
                                      value={
                                        element.subTitleFontWeight ??
                                        element.fontWeight ??
                                        fontWeights[0].value
                                      }
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          subTitleFontWeight:
                                            event.target.value,
                                        })
                                      }
                                    >
                                      {fontWeights.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label>
                                    Размер шрифта подзаголовка
                                    <input
                                      type="number"
                                      min={10}
                                      max={120}
                                      value={
                                        element.subTitleFontSize ??
                                        (element.fontSize
                                          ? Math.max(
                                              10,
                                              Math.round(
                                                element.fontSize * 0.65,
                                              ),
                                            )
                                          : 24)
                                      }
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          subTitleFontSize:
                                            Number(event.target.value) ||
                                            undefined,
                                        })
                                      }
                                    />
                                  </label>
                                  <label>
                                    Высота строки подзаголовка (px)
                                    <input
                                      type="number"
                                      min={10}
                                      max={200}
                                      value={element.subTitleLineHeight ?? ""}
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          subTitleLineHeight: parseNumberInput(
                                            event.target.value,
                                          ),
                                        })
                                      }
                                    />
                                  </label>
                                  {(() => {
                                    const subTitleTextColor =
                                      parseColorWithAlpha(
                                        element.subTitleTextColor ??
                                          element.textColor ??
                                          undefined,
                                        "#ffffff",
                                      );
                                    return (
                                      <div className={classes.gradientField}>
                                        <span>Цвет текста подзаголовка</span>
                                        <input
                                          type="color"
                                          value={subTitleTextColor.hex}
                                          onChange={(event) =>
                                            updateTabloElement(key, {
                                              subTitleTextColor:
                                                buildColorWithAlpha(
                                                  event.target.value,
                                                  subTitleTextColor.alpha,
                                                ),
                                            })
                                          }
                                        />
                                        <span>Непрозрачность</span>
                                        <div className={classes.rangeRow}>
                                          <input
                                            type="range"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={Math.round(
                                              subTitleTextColor.alpha * 100,
                                            )}
                                            onChange={(event) =>
                                              updateTabloElement(key, {
                                                subTitleTextColor:
                                                  buildColorWithAlpha(
                                                    subTitleTextColor.hex,
                                                    clampNumber(
                                                      Number(
                                                        event.target.value,
                                                      ),
                                                      0,
                                                      100,
                                                    ) / 100,
                                                  ),
                                              })
                                            }
                                          />
                                          <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={Math.round(
                                              subTitleTextColor.alpha * 100,
                                            )}
                                            onChange={(event) =>
                                              updateTabloElement(key, {
                                                subTitleTextColor:
                                                  buildColorWithAlpha(
                                                    subTitleTextColor.hex,
                                                    clampNumber(
                                                      Number(
                                                        event.target.value,
                                                      ),
                                                      0,
                                                      100,
                                                    ) / 100,
                                                  ),
                                              })
                                            }
                                          />
                                          <span>%</span>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </>
                              )}
                              {elementHasText[key] && key !== "title" && (
                                <>
                                  <label>
                                    Шрифт
                                    <select
                                      value={element.fontFamily ?? ""}
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          fontFamily: event.target.value,
                                        })
                                      }
                                    >
                                      {fontOptions.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label>
                                    Начертание
                                    <select
                                      value={element.fontWeight ?? ""}
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          fontWeight: event.target.value,
                                        })
                                      }
                                    >
                                      {fontWeights.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label>
                                    Размер шрифта
                                    <input
                                      type="number"
                                      min={10}
                                      max={120}
                                      value={element.fontSize ?? 16}
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          fontSize: Number(event.target.value),
                                        })
                                      }
                                    />
                                  </label>
                                  <div className={classes.gradientField}>
                                    <span>Цвет текста</span>
                                    <input
                                      type="color"
                                      value={elementTextColor.hex}
                                      onChange={(event) =>
                                        updateTabloElement(key, {
                                          textColor: buildColorWithAlpha(
                                            event.target.value,
                                            elementTextColor.alpha,
                                          ),
                                        })
                                      }
                                    />
                                    <span>Непрозрачность</span>
                                    <div className={classes.rangeRow}>
                                      <input
                                        type="range"
                                        min={0}
                                        max={100}
                                        step={1}
                                        value={Math.round(
                                          elementTextColor.alpha * 100,
                                        )}
                                        onChange={(event) =>
                                          updateTabloElement(key, {
                                            textColor: buildColorWithAlpha(
                                              elementTextColor.hex,
                                              clampNumber(
                                                Number(event.target.value),
                                                0,
                                                100,
                                              ) / 100,
                                            ),
                                          })
                                        }
                                      />
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={Math.round(
                                          elementTextColor.alpha * 100,
                                        )}
                                        onChange={(event) =>
                                          updateTabloElement(key, {
                                            textColor: buildColorWithAlpha(
                                              elementTextColor.hex,
                                              clampNumber(
                                                Number(event.target.value),
                                                0,
                                                100,
                                              ) / 100,
                                            ),
                                          })
                                        }
                                      />
                                      <span>%</span>
                                    </div>
                                  </div>
                                </>
                              )}
                              {key === "ticker" && (
                                <>
                                  <div className={classes.tickerTextsBlock}>
                                    <span>Тексты бегущей строки</span>
                                    {(element.tickerTexts ?? []).map(
                                      (line, idx) => (
                                        <div
                                          key={idx}
                                          className={classes.tickerTextRow}
                                        >
                                          <input
                                            type="text"
                                            value={line}
                                            onChange={(event) => {
                                              const arr = [
                                                ...(element.tickerTexts ?? []),
                                              ];
                                              arr[idx] = event.target.value;
                                              updateTabloElement(key, {
                                                tickerTexts: arr,
                                              });
                                            }}
                                            placeholder="Текст строки"
                                          />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const arr = (
                                                element.tickerTexts ?? []
                                              ).filter((_, i) => i !== idx);
                                              updateTabloElement(key, {
                                                tickerTexts: arr,
                                              });
                                            }}
                                            aria-label="Удалить строку"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ),
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const arr = [
                                          ...(element.tickerTexts ?? []),
                                          "",
                                        ];
                                        updateTabloElement(key, {
                                          tickerTexts: arr,
                                        });
                                      }}
                                    >
                                      Добавить строку
                                    </button>
                                  </div>
                                  <label>
                                    Скорость бегущей строки (секунд на полный
                                    цикл)
                                    <input
                                      type="number"
                                      min={10}
                                      step={1}
                                      value={tickerSpeedInput}
                                      onChange={(event) => {
                                        const raw = event.target.value;
                                        setTickerSpeedInput(raw);
                                        if (form && raw.trim() !== "") {
                                          const value = Number(raw.trim());
                                          if (Number.isFinite(value)) {
                                            const next = Math.max(10, value);
                                            setForm({
                                              ...form,
                                              tickerSpeed: next,
                                            });
                                          }
                                        }
                                      }}
                                      onBlur={() => {
                                        if (!form) return;
                                        const raw = tickerSpeedInput.trim();
                                        if (!raw) {
                                          const fallback =
                                            form.tickerSpeed ??
                                            defaultTickerSpeedSeconds;
                                          const next = Math.max(10, fallback);
                                          setForm({
                                            ...form,
                                            tickerSpeed: next,
                                          });
                                          setTickerSpeedInput(String(next));
                                          return;
                                        }
                                        const value = Number(raw);
                                        if (!Number.isFinite(value)) {
                                          const fallback =
                                            form.tickerSpeed ??
                                            defaultTickerSpeedSeconds;
                                          setTickerSpeedInput(String(fallback));
                                          return;
                                        }
                                        const next = Math.max(10, value);
                                        setForm({
                                          ...form,
                                          tickerSpeed: next,
                                        });
                                        setTickerSpeedInput(String(next));
                                      }}
                                    />
                                  </label>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            )}
          </section>

          <section className={classes.section}>
            <button
              type="button"
              className={classes.sectionHeader}
              onClick={() => toggleSectionExpanded("volume")}
            >
              <h3>Громкость видео</h3>
              <span
                className={`${classes.collapseIcon} ${
                  sectionsExpanded.volume ? classes.collapseIconExpanded : ""
                }`}
                aria-hidden="true"
              >
                {sectionsExpanded.volume ? "▲" : "▼"}
              </span>
            </button>
            {sectionsExpanded.volume && (
              <div className={classes.grid}>
                <label className={classes.rangeField}>
                  Громкость воспроизведения
                  <div className={classes.rangeRow}>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={videoVolume}
                      onChange={(event) =>
                        setVideoVolume(Number(event.target.value))
                      }
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={videoVolume}
                      onChange={(event) =>
                        setVideoVolume(Number(event.target.value))
                      }
                    />
                    <span>%</span>
                  </div>
                </label>
              </div>
            )}
          </section>

          <div className={classes.actions}>
            <CustomButton
              content="Сохранить"
              styleType="active"
              onClick={handleSaveClick}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TabloSettingsPage;
